<template>
  <div class="card">
    <h3>Info Ticket</h3>
    <Loader v-if="loading" />
    <template v-else-if="areas.length > 0 || categories.length > 0">
      <div class="filter-buttons">
        <button
          type="button"
          class="filter-btn"
          :class="{ active: searchMode === 'area' }"
          :disabled="areas.length === 0"
          @click="onSearchModeSelect('area')"
        >
          <i class="pi pi-map-marker"></i>
          Cerca per area
        </button>
        <button
          type="button"
          class="filter-btn"
          :class="{ active: searchMode === 'category' }"
          :disabled="categories.length === 0"
          @click="onSearchModeSelect('category')"
        >
          <i class="pi pi-tag"></i>
          Cerca per categoria
        </button>

        <Button class="p-button-text" icon="pi pi-info" label="Statistiche" @click="getStatistiche()"/>
        <button
          type="button"
          class="filter-btn"
          :class="{ active: searchMode === 'agenti' }"
          @click="onSearchModeSelect('agenti')"
        >
          <i class="pi pi-users"></i>
          Statistiche Agenti
        </button>
        <button
          type="button"
          class="filter-btn"
          :class="{ active: searchMode === 'categorie' }"
          @click="onSearchModeSelect('categorie')"
        >
          <i class="pi pi-chart-bar"></i>
          Statistiche categorie
        </button>
        <button
          type="button"
          class="filter-btn"
          :class="{ active: searchMode === 'trasferimenti' }"
          @click="onSearchModeSelect('trasferimenti')"
        >
          <i class="pi pi-phone"></i>
          Statistiche trasferimenti
        </button>
        <button
          type="button"
          class="filter-btn"
          :class="{ active: searchMode === 'distribuzione' }"
          @click="onSearchModeSelect('distribuzione')"
        >
          <i class="pi pi-sitemap"></i>
          Distribuzione
        </button>
      </div>

      <div v-if="searchMode === 'agenti'" class="tab-content statistiche-agenti-section">
        <h4 class="statistiche-agenti-title">Statistiche Agenti</h4>

        <div class="statistiche-agenti-filters">
          <div class="field-group">
            <label for="statisticheAgenteSelect">Agente</label>
            <Dropdown
              id="statisticheAgenteSelect"
              v-model="selectedAgenteStats"
              :options="agentiStats"
              optionLabel="name"
              placeholder="Seleziona agente"
              filter
              class="w-full"
              :loading="loadingAgentiStats"
            />
          </div>

          <div class="field-group">
            <label for="statisticheAgentiDataDa">Dal</label>
            <Calendar
              id="statisticheAgentiDataDa"
              v-model="statisticheAgentiDataDa"
              dateFormat="dd/mm/yy"
              :showIcon="true"
              :manualInput="false"
              :maxDate="statisticheAgentiDataA"
              class="w-full"
            />
          </div>

          <div class="field-group">
            <label for="statisticheAgentiDataA">Al</label>
            <Calendar
              id="statisticheAgentiDataA"
              v-model="statisticheAgentiDataA"
              dateFormat="dd/mm/yy"
              :showIcon="true"
              :manualInput="false"
              :minDate="statisticheAgentiDataDa"
              :maxDate="maxStatisticheAgentiDate"
              class="w-full"
            />
          </div>

          <Button
            icon="pi pi-search"
            label="Cerca"
            :loading="loadingStatisticheAgenti"
            :disabled="!selectedAgenteStats || loadingStatisticheAgenti"
            @click="loadStatisticheAgenti"
          />
        </div>

        <div v-if="loadingAgentiStats || loadingStatisticheAgenti" class="content-loading">
          <ProgressSpinner style="width: 50px; height: 50px" strokeWidth="4" />
        </div>

        <template v-else>
          <div v-if="statisticheAgentiError" class="response-box response-box--error mt-3">
            <p class="feedback-message">{{ statisticheAgentiError }}</p>
          </div>

          <template v-else-if="statisticheAgenti">
            <div class="statistiche-agenti-summary">
              <div class="summary-item">
                <span class="summary-label">Agente</span>
                <span class="summary-value">{{ statisticheAgenti.agente }}</span>
              </div>
              <div class="summary-item">
                <span class="summary-label">Periodo</span>
                <span class="summary-value">
                  {{ formatStatsDate(statisticheAgenti.periodo?.data_da) }}
                  -
                  {{ formatStatsDate(statisticheAgenti.periodo?.data_a) }}
                </span>
              </div>
            </div>

            <DataTable
              :value="statisticheAgentiRows"
              showGridlines
              scrollable
              scrollHeight="200px"
              class="mt-3 statistiche-agenti-table"
            >
              <template #header>
                <span class="font-semibold">Riepilogo totale</span>
              </template>
              <Column
                v-for="col of colonneStatisticheAgenti"
                :key="col.field"
                :field="col.field"
                :header="col.header"
                style="min-width: 120px"
              />
            </DataTable>

            <DataTable
              v-if="statisticheAgentiCategorie.length > 0"
              :value="statisticheAgentiCategorieFiltrate"
              dataKey="id_category_motivation"
              showGridlines
              scrollable
              scrollHeight="320px"
              class="mt-4 statistiche-agenti-table"
            >
              <template #header>
                <div class="statistiche-agenti-categorie-header">
                  <span class="font-semibold">Dettaglio per categoria</span>
                  <div class="statistiche-agenti-categorie-filters">
                    <Dropdown
                      v-model="selectedCategoriaStatsFilter"
                      :options="categorieStatsFilterOptions"
                      optionLabel="label"
                      optionValue="value"
                      placeholder="Tutte le categorie"
                      showClear
                      filter
                      class="categoria-stats-filter"
                    />
                  </div>
                </div>
              </template>
              <template #empty>
                <span class="text-color-secondary">Nessuna categoria corrisponde al filtro selezionato.</span>
              </template>
              <Column
                v-for="col of colonneStatisticheAgentiCategorie"
                :key="col.field"
                :field="col.field"
                :header="col.header"
                :style="col.field === 'categoria' ? { minWidth: '220px' } : { minWidth: '120px' }"
              />
            </DataTable>

            <DataTable
              v-if="(gestioneTicketPagination?.total || 0) > 0"
              :value="gestioneTicketAgentiFiltrati"
              dataKey="id_ticket"
              showGridlines
              scrollable
              scrollHeight="320px"
              paginator
              :rows="gestioneTicketPerPage"
              :totalRecords="gestioneTicketPagination?.total || 0"
              :first="((gestioneTicketPagination?.current_page || 1) - 1) * gestioneTicketPerPage"
              :lazy="true"
              :loading="loadingGestioneTicket"
              :rowsPerPageOptions="[5, 15, 25, 50]"
              paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
              currentPageReportTemplate="{first} - {last} di {totalRecords}"
              class="mt-4 statistiche-agenti-table gestione-ticket-table-clickable"
              @page="onGestioneTicketPageChange"
              @row-click="onGestioneTicketRowClick"
            >
              <template #header>
                <span class="font-semibold">
                  Gestione ticket
                  <span v-if="selectedCategoriaStatsFilter != null" class="gestione-ticket-filter-hint">
                    (filtrati per categoria)
                  </span>
                </span>
              </template>
              <template #empty>
                <span class="text-color-secondary">Nessun ticket corrisponde al filtro categoria selezionato.</span>
              </template>
              <Column
                v-for="col of colonneGestioneTicketAgenti"
                :key="col.field"
                :field="col.field"
                :header="col.header"
                :style="getGestioneColStyle(col.field)"
              >
                <template v-if="col.field === 'id_ticket'" #body="{ data }">
                  <span class="gestione-ticket-id">#{{ data.id_ticket }}</span>
                </template>
                <template v-else-if="col.field === 'stato_attuale'" #body="{ data }">
                  <span
                    class="status-badge"
                    :class="`status-badge--${data.stato_attuale_code || 'default'}`"
                  >
                    {{ data.stato_attuale || '-' }}
                  </span>
                </template>
                <template v-else-if="col.field === 'tempo_nello_stato_attuale'" #body="{ data }">
                  <span>{{ data.tempo_nello_stato_attuale || '-' }}</span>
                  <Tag
                    v-if="isTempoGestioneInCorso(data)"
                    value="In corso"
                    severity="info"
                    class="ml-2"
                  />
                </template>
              </Column>
            </DataTable>
          </template>

          <p v-else class="text-color-secondary mt-3">Imposta i filtri e clicca Cerca per visualizzare le statistiche.</p>
        </template>
      </div>

      <div v-if="searchMode === 'categorie'" class="tab-content statistiche-agenti-section">
        <h4 class="statistiche-agenti-title">Statistiche categorie</h4>

        <div class="statistiche-agenti-filters">
          <div class="field-group">
            <label for="statisticheCategorieDataDa">Dal</label>
            <Calendar
              id="statisticheCategorieDataDa"
              v-model="statisticheCategorieDataDa"
              dateFormat="dd/mm/yy"
              :showIcon="true"
              :manualInput="false"
              :maxDate="statisticheCategorieDataA"
              class="w-full"
            />
          </div>

          <div class="field-group">
            <label for="statisticheCategorieDataA">Al</label>
            <Calendar
              id="statisticheCategorieDataA"
              v-model="statisticheCategorieDataA"
              dateFormat="dd/mm/yy"
              :showIcon="true"
              :manualInput="false"
              :minDate="statisticheCategorieDataDa"
              :maxDate="maxStatisticheCategorieDate"
              class="w-full"
            />
          </div>

          <Button
            icon="pi pi-search"
            label="Cerca"
            :loading="loadingStatisticheCategorie"
            :disabled="loadingStatisticheCategorie"
            @click="loadStatisticheCategorie"
          />
        </div>

        <div v-if="loadingStatisticheCategorie" class="content-loading">
          <ProgressSpinner style="width: 50px; height: 50px" strokeWidth="4" />
        </div>

        <template v-else>
          <div v-if="statisticheCategorieError" class="response-box response-box--error mt-3">
            <p class="feedback-message">{{ statisticheCategorieError }}</p>
          </div>

          <template v-else-if="statisticheCategorie">
            <div class="stats-categorie-period-grid">
              <div class="stats-period-card">
                <div class="stats-period-card-icon">
                  <i class="pi pi-calendar"></i>
                </div>
                <div class="stats-period-card-content">
                  <span class="stats-period-card-label">Periodo selezionato</span>
                  <span class="stats-period-card-value">
                    {{ formatStatsDate(statisticheCategorie.periodo?.data_da) }}
                    -
                    {{ formatStatsDate(statisticheCategorie.periodo?.data_a) }}
                  </span>
                </div>
              </div>
              <div class="stats-period-card stats-period-card--muted">
                <div class="stats-period-card-icon">
                  <i class="pi pi-history"></i>
                </div>
                <div class="stats-period-card-content">
                  <span class="stats-period-card-label">Confronto mese precedente</span>
                  <span class="stats-period-card-value">
                    {{ formatStatsDate(statisticheCategorie.mese_precedente?.data_da) }}
                    -
                    {{ formatStatsDate(statisticheCategorie.mese_precedente?.data_a) }}
                  </span>
                </div>
              </div>
            </div>

            <div v-if="statisticheCategorieRiepilogoRow" class="stats-categorie-kpi-grid">
              <div class="stats-kpi-card stats-kpi-card--total">
                <span class="stats-kpi-label">Totale ticket</span>
                <span class="stats-kpi-value">{{ statisticheCategorieRiepilogoRow.totale }}</span>
                <span
                  class="stats-compare-badge"
                  :class="getConfrontoClass(statisticheCategorieRiepilogoRow.confronto_totale)"
                >
                  <i :class="getConfrontoIcon(statisticheCategorieRiepilogoRow.confronto_totale)"></i>
                  {{ formatConfronto(statisticheCategorieRiepilogoRow.confronto_totale) }}
                </span>
              </div>
              <div class="stats-kpi-card stats-kpi-card--opened">
                <span class="stats-kpi-label">Aperti</span>
                <span class="stats-kpi-value">{{ statisticheCategorieRiepilogoRow.aperti }}</span>
                <span class="stats-kpi-sub">{{ formatPercent(statisticheCategorieRiepilogoRow.percentuale_aperti) }}</span>
              </div>
              <div class="stats-kpi-card stats-kpi-card--progress">
                <span class="stats-kpi-label">In lavorazione</span>
                <span class="stats-kpi-value">{{ statisticheCategorieRiepilogoRow.in_lavorazione }}</span>
                <span class="stats-kpi-sub">{{ formatPercent(statisticheCategorieRiepilogoRow.percentuale_in_lavorazione) }}</span>
              </div>
              <div class="stats-kpi-card stats-kpi-card--closed">
                <span class="stats-kpi-label">Chiusi</span>
                <span class="stats-kpi-value">{{ statisticheCategorieRiepilogoRow.chiusi }}</span>
                <span class="stats-kpi-sub">{{ formatPercent(statisticheCategorieRiepilogoRow.percentuale_chiusi) }}</span>
              </div>
            </div>

            <div class="stats-categorie-table-panel">
              <div class="stats-categorie-table-header">
                <div>
                  <h5 class="stats-categorie-table-title">Dettaglio per categoria</h5>
                  <p class="stats-categorie-table-subtitle">
                    {{ statisticheCategorieRows.length }} categorie nel periodo selezionato
                  </p>
                </div>
              </div>

              <DataTable
                :value="statisticheCategorieRows"
                dataKey="id_category_motivation"
                scrollable
                scrollHeight="460px"
                :rowClass="getCategorieRowClass"
                class="stats-categorie-table stats-categorie-table-clickable"
                @row-click="onCategoriaStatsRowClick"
              >
                <template #empty>
                  <div class="stats-categorie-empty">
                    <i class="pi pi-chart-bar"></i>
                    <span>Nessuna categoria trovata per il periodo selezionato.</span>
                  </div>
                </template>
                <Column header="Categoria" style="min-width: 240px">
                  <template #body="{ data }">
                    <div class="stats-category-cell">
                      <span
                        class="stats-category-dot"
                        :class="data.id_category_motivation === 0 ? 'stats-category-dot--muted' : 'stats-category-dot--accent'"
                      ></span>
                      <div class="stats-category-text">
                        <span class="stats-category-name">{{ data.categoria }}</span>
                        <span class="stats-category-share">
                          {{ formatPercent(data.percentuale_sul_totale) }} sul totale
                        </span>
                      </div>
                    </div>
                  </template>
                </Column>
                <Column header="Totale" style="min-width: 150px">
                  <template #body="{ data }">
                    <div class="stats-metric-cell">
                      <span class="stats-metric-value">{{ data.totale }}</span>
                      <div class="stats-metric-bar">
                        <div
                          class="stats-metric-bar-fill stats-metric-bar-fill--total"
                          :style="getMetricBarStyle(data.percentuale_sul_totale)"
                        ></div>
                      </div>
                    </div>
                  </template>
                </Column>
                <Column header="vs mese prec." style="min-width: 140px">
                  <template #body="{ data }">
                    <span class="stats-compare-badge" :class="getConfrontoClass(data.confronto_totale)">
                      <i :class="getConfrontoIcon(data.confronto_totale)"></i>
                      {{ formatConfronto(data.confronto_totale) }}
                    </span>
                  </template>
                </Column>
                <Column header="Aperti" style="min-width: 130px">
                  <template #body="{ data }">
                    <span class="stats-status-pill stats-status-pill--opened">
                      {{ data.aperti }}
                      <small>{{ formatPercent(data.percentuale_aperti) }}</small>
                    </span>
                  </template>
                </Column>
                <Column header="In lavorazione" style="min-width: 150px">
                  <template #body="{ data }">
                    <span class="stats-status-pill stats-status-pill--progress">
                      {{ data.in_lavorazione }}
                      <small>{{ formatPercent(data.percentuale_in_lavorazione) }}</small>
                    </span>
                  </template>
                </Column>
                <Column header="Chiusi" style="min-width: 130px">
                  <template #body="{ data }">
                    <span class="stats-status-pill stats-status-pill--closed">
                      {{ data.chiusi }}
                      <small>{{ formatPercent(data.percentuale_chiusi) }}</small>
                    </span>
                  </template>
                </Column>
                <Column header="Mese prec." style="min-width: 110px">
                  <template #body="{ data }">
                    <span class="stats-prev-month">{{ data.totale_mese_precedente }}</span>
                  </template>
                </Column>
              </DataTable>
            </div>
          </template>

          <p v-else class="text-color-secondary mt-3">Imposta i filtri e clicca Cerca per visualizzare le statistiche.</p>
        </template>
      </div>

      <div v-if="searchMode === 'trasferimenti'" class="tab-content statistiche-agenti-section">
        <h4 class="statistiche-agenti-title">Statistiche trasferimenti</h4>

        <div class="statistiche-agenti-filters">
          <div class="field-group">
            <label for="statisticheTrasferimentiDataDa">Dal</label>
            <Calendar
              id="statisticheTrasferimentiDataDa"
              v-model="statisticheTrasferimentiDataDa"
              dateFormat="dd/mm/yy"
              :showIcon="true"
              :manualInput="false"
              :maxDate="statisticheTrasferimentiDataA"
              class="w-full"
            />
          </div>

          <div class="field-group">
            <label for="statisticheTrasferimentiDataA">Al</label>
            <Calendar
              id="statisticheTrasferimentiDataA"
              v-model="statisticheTrasferimentiDataA"
              dateFormat="dd/mm/yy"
              :showIcon="true"
              :manualInput="false"
              :minDate="statisticheTrasferimentiDataDa"
              :maxDate="maxStatisticheTrasferimentiDate"
              class="w-full"
            />
          </div>

          <Button
            icon="pi pi-search"
            label="Cerca"
            :loading="loadingStatisticheTrasferimenti"
            :disabled="loadingStatisticheTrasferimenti"
            @click="loadStatisticheTrasferimenti"
          />
        </div>

        <div v-if="loadingStatisticheTrasferimenti" class="content-loading">
          <ProgressSpinner style="width: 50px; height: 50px" strokeWidth="4" />
        </div>

        <template v-else>
          <div v-if="statisticheTrasferimentiError" class="response-box response-box--error mt-3">
            <p class="feedback-message">{{ statisticheTrasferimentiError }}</p>
          </div>

          <template v-else-if="statisticheTrasferimenti">
            <div class="stats-categorie-period-grid">
              <div class="stats-period-card">
                <div class="stats-period-card-icon">
                  <i class="pi pi-calendar"></i>
                </div>
                <div class="stats-period-card-content">
                  <span class="stats-period-card-label">Periodo selezionato</span>
                  <span class="stats-period-card-value">
                    {{ formatStatsDate(statisticheTrasferimenti.periodo?.data_da) }}
                    -
                    {{ formatStatsDate(statisticheTrasferimenti.periodo?.data_a) }}
                  </span>
                </div>
              </div>
              <div class="stats-period-card stats-period-card--muted">
                <div class="stats-period-card-icon">
                  <i class="pi pi-history"></i>
                </div>
                <div class="stats-period-card-content">
                  <span class="stats-period-card-label">Confronto mese precedente</span>
                  <span class="stats-period-card-value">
                    {{ formatStatsDate(statisticheTrasferimenti.mese_precedente?.data_da) }}
                    -
                    {{ formatStatsDate(statisticheTrasferimenti.mese_precedente?.data_a) }}
                  </span>
                </div>
              </div>
            </div>

            <div v-if="statisticheTrasferimentiRiepilogo" class="stats-trasferimenti-kpi-grid">
              <div class="stats-trasferimenti-kpi-card stats-trasferimenti-kpi-card--received">
                <div class="stats-trasferimenti-kpi-head">
                  <span class="stats-kpi-label">Trasferimenti ricevuti</span>
                  <span
                    class="stats-compare-badge"
                    :class="getConfrontoClass(statisticheTrasferimentiRiepilogo.confronto_ricevuti)"
                  >
                    <i :class="getConfrontoIcon(statisticheTrasferimentiRiepilogo.confronto_ricevuti)"></i>
                    {{ formatConfronto(statisticheTrasferimentiRiepilogo.confronto_ricevuti) }}
                  </span>
                </div>
                <span class="stats-kpi-value">{{ statisticheTrasferimentiRiepilogo.ricevuti.totale }}</span>
                <div class="stats-trasferimenti-kpi-breakdown">
                  <span class="stats-transfer-pill stats-transfer-pill--success">
                    Riusciti {{ statisticheTrasferimentiRiepilogo.ricevuti.con_successo }}
                    <small>{{ formatPercent(statisticheTrasferimentiRiepilogo.ricevuti.percentuale_con_successo) }}</small>
                  </span>
                  <span class="stats-transfer-pill stats-transfer-pill--failed">
                    Falliti {{ statisticheTrasferimentiRiepilogo.ricevuti.falliti }}
                    <small>{{ formatPercent(statisticheTrasferimentiRiepilogo.ricevuti.percentuale_falliti) }}</small>
                  </span>
                </div>
              </div>

              <div class="stats-trasferimenti-kpi-card stats-trasferimenti-kpi-card--made">
                <div class="stats-trasferimenti-kpi-head">
                  <span class="stats-kpi-label">Trasferimenti effettuati</span>
                  <span
                    class="stats-compare-badge"
                    :class="getConfrontoClass(statisticheTrasferimentiRiepilogo.confronto_effettuati)"
                  >
                    <i :class="getConfrontoIcon(statisticheTrasferimentiRiepilogo.confronto_effettuati)"></i>
                    {{ formatConfronto(statisticheTrasferimentiRiepilogo.confronto_effettuati) }}
                  </span>
                </div>
                <span class="stats-kpi-value">{{ statisticheTrasferimentiRiepilogo.effettuati.totale }}</span>
                <div class="stats-trasferimenti-kpi-breakdown">
                  <span class="stats-transfer-pill stats-transfer-pill--success">
                    Riusciti {{ statisticheTrasferimentiRiepilogo.effettuati.con_successo }}
                    <small>{{ formatPercent(statisticheTrasferimentiRiepilogo.effettuati.percentuale_con_successo) }}</small>
                  </span>
                  <span class="stats-transfer-pill stats-transfer-pill--failed">
                    Falliti {{ statisticheTrasferimentiRiepilogo.effettuati.falliti }}
                    <small>{{ formatPercent(statisticheTrasferimentiRiepilogo.effettuati.percentuale_falliti) }}</small>
                  </span>
                </div>
              </div>
            </div>

            <div class="stats-categorie-table-panel">
              <div class="stats-categorie-table-header">
                <div>
                  <h5 class="stats-categorie-table-title">Dettaglio per reparto</h5>
                  <p class="stats-categorie-table-subtitle">
                    {{ statisticheTrasferimentiRows.length }} reparti nel periodo selezionato
                  </p>
                </div>
              </div>

              <DataTable
                :value="statisticheTrasferimentiRows"
                dataKey="id_ticket_area"
                scrollable
                scrollHeight="520px"
                :rowClass="getTrasferimentiRowClass"
                class="stats-categorie-table stats-trasferimenti-table"
              >
                <template #empty>
                  <div class="stats-categorie-empty">
                    <i class="pi pi-phone"></i>
                    <span>Nessun trasferimento trovato per il periodo selezionato.</span>
                  </div>
                </template>
                <Column header="Reparto" style="min-width: 220px" frozen>
                  <template #body="{ data }">
                    <div class="stats-category-cell">
                      <span
                        class="stats-category-dot"
                        :class="data.id_ticket_area === 0 ? 'stats-category-dot--muted' : 'stats-category-dot--accent'"
                      ></span>
                      <div class="stats-category-text">
                        <span class="stats-category-name">{{ data.reparto }}</span>
                        <span v-if="data.area_code" class="stats-category-share">{{ data.area_code }}</span>
                      </div>
                    </div>
                  </template>
                </Column>
                <Column header="Ricevuti" style="min-width: 120px">
                  <template #body="{ data }">
                    <div class="stats-metric-cell">
                      <span class="stats-metric-value">{{ data.ricevuti_totale }}</span>
                      <div class="stats-metric-bar">
                        <div
                          class="stats-metric-bar-fill stats-metric-bar-fill--received"
                          :style="getMetricBarStyle(data.ricevuti_percentuale_sul_totale)"
                        ></div>
                      </div>
                      <span class="stats-category-share">{{ formatPercent(data.ricevuti_percentuale_sul_totale) }} sul totale</span>
                    </div>
                  </template>
                </Column>
                <Column header="Ricev. vs prec." style="min-width: 130px">
                  <template #body="{ data }">
                    <span class="stats-compare-badge" :class="getConfrontoClass(data.ricevuti_confronto_totale)">
                      <i :class="getConfrontoIcon(data.ricevuti_confronto_totale)"></i>
                      {{ formatConfronto(data.ricevuti_confronto_totale) }}
                    </span>
                  </template>
                </Column>
                <Column header="Ricev. riusciti" style="min-width: 130px">
                  <template #body="{ data }">
                    <span class="stats-transfer-pill stats-transfer-pill--success">
                      {{ data.ricevuti_con_successo }}
                      <small>{{ formatPercent(data.ricevuti_percentuale_con_successo) }}</small>
                    </span>
                  </template>
                </Column>
                <Column header="Ricev. falliti" style="min-width: 120px">
                  <template #body="{ data }">
                    <span class="stats-transfer-pill stats-transfer-pill--failed">
                      {{ data.ricevuti_falliti }}
                      <small>{{ formatPercent(data.ricevuti_percentuale_falliti) }}</small>
                    </span>
                  </template>
                </Column>
                <Column header="Effettuati" style="min-width: 120px">
                  <template #body="{ data }">
                    <div class="stats-metric-cell">
                      <span class="stats-metric-value">{{ data.effettuati_totale }}</span>
                      <div class="stats-metric-bar">
                        <div
                          class="stats-metric-bar-fill stats-metric-bar-fill--made"
                          :style="getMetricBarStyle(data.effettuati_percentuale_sul_totale)"
                        ></div>
                      </div>
                      <span class="stats-category-share">{{ formatPercent(data.effettuati_percentuale_sul_totale) }} sul totale</span>
                    </div>
                  </template>
                </Column>
                <Column header="Eff. vs prec." style="min-width: 130px">
                  <template #body="{ data }">
                    <span class="stats-compare-badge" :class="getConfrontoClass(data.effettuati_confronto_totale)">
                      <i :class="getConfrontoIcon(data.effettuati_confronto_totale)"></i>
                      {{ formatConfronto(data.effettuati_confronto_totale) }}
                    </span>
                  </template>
                </Column>
                <Column header="Eff. riusciti" style="min-width: 130px">
                  <template #body="{ data }">
                    <span class="stats-transfer-pill stats-transfer-pill--success">
                      {{ data.effettuati_con_successo }}
                      <small>{{ formatPercent(data.effettuati_percentuale_con_successo) }}</small>
                    </span>
                  </template>
                </Column>
                <Column header="Eff. falliti" style="min-width: 120px">
                  <template #body="{ data }">
                    <span class="stats-transfer-pill stats-transfer-pill--failed">
                      {{ data.effettuati_falliti }}
                      <small>{{ formatPercent(data.effettuati_percentuale_falliti) }}</small>
                    </span>
                  </template>
                </Column>
                <Column header="Mese prec." style="min-width: 110px">
                  <template #body="{ data }">
                    <div class="stats-prev-month-block">
                      <span>R {{ data.ricevuti_mese_precedente }}</span>
                      <span>E {{ data.effettuati_mese_precedente }}</span>
                    </div>
                  </template>
                </Column>
              </DataTable>
            </div>
          </template>

          <p v-else class="text-color-secondary mt-3">Imposta i filtri e clicca Cerca per visualizzare le statistiche.</p>
        </template>
      </div>

      <div v-if="searchMode === 'distribuzione'" class="tab-content statistiche-agenti-section">
        <div class="statistiche-agenti-filters">
          <h4 class="statistiche-agenti-title" style="margin: 0;">Distribuzione Assistenza Clienti</h4>
          <Button
            icon="pi pi-refresh"
            label="Aggiorna"
            :loading="loadingDistribuzione || loadingDistribuzioneTabelle"
            @click="aggiornaDistribuzione"
          />
        </div>

        <div v-if="loadingDistribuzione" class="content-loading">
          <ProgressSpinner style="width: 50px; height: 50px" strokeWidth="4" />
        </div>

        <div v-else-if="distribuzioneError" class="response-box response-box--error mt-3">
          <p class="feedback-message">{{ distribuzioneError }}</p>
        </div>

        <template v-else-if="distribuzione">
          <p v-if="distribuzione.limite" class="text-color-secondary mt-3">
            Prova in corso: ogni esecuzione sposta e assegna al massimo {{ distribuzione.limite }} ticket.
          </p>
          <div class="statistiche-agenti-summary">
            <div class="summary-item">
              <span class="summary-label">Aggiornato</span>
              <span class="summary-value">{{ formatDateTime(distribuzione.aggiornato_il) }}</span>
            </div>
            <div class="summary-item">
              <span class="summary-label">Da incanalare</span>
              <span class="summary-value">{{ distribuzione.coda_da_incanalare }}</span>
            </div>
            <div class="summary-item">
              <span class="summary-label">In coda da assegnare</span>
              <span class="summary-value">{{ distribuzione.coda_da_assegnare }}</span>
            </div>
            <div class="summary-item">
              <span class="summary-label">Disponibili</span>
              <span class="summary-value">{{ distribuzione.operatori_disponibili }}</span>
            </div>
            <div class="summary-item">
              <span class="summary-label">Assenti ora</span>
              <span class="summary-value">{{ distribuzione.operatori_assenti }}</span>
            </div>
            <div class="summary-item">
              <span class="summary-label">Scostamento carico</span>
              <span class="summary-value">{{ distribuzione.scostamento_carico }}</span>
            </div>
          </div>

          <DataTable
            :value="distribuzione.operatori || []"
            dataKey="id"
            showGridlines
            class="mt-3 statistiche-agenti-table"
          >
            <template #header>
              <span class="font-semibold">Operatori</span>
            </template>
            <Column field="nome" header="Operatore" style="min-width: 220px" />
            <Column field="aperti" header="Aperti" style="min-width: 100px" />
            <Column field="in_lavorazione" header="In lavorazione" style="min-width: 140px" />
            <Column field="totale" header="Totale" style="min-width: 100px" />
            <Column header="Stato" style="min-width: 220px">
              <template #body="{ data }">
                <span v-if="data.disponibile">Disponibile</span>
                <span v-else>{{ assenzaLabel(data.assenza) }}</span>
              </template>
            </Column>
          </DataTable>

          <DataTable
            :value="distribuzione.da_incanalare?.data || []"
            dataKey="id_ticket"
            showGridlines
            class="mt-4 statistiche-agenti-table"
            paginator
            lazy
            :rows="distribuzionePagine.incanalare.perPage"
            :totalRecords="distribuzione.da_incanalare?.pagination?.total || 0"
            :first="((distribuzione.da_incanalare?.pagination?.current_page || 1) - 1) * distribuzionePagine.incanalare.perPage"
            :loading="loadingDistribuzioneTabelle"
            :rowsPerPageOptions="[15, 25, 50]"
            paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
            currentPageReportTemplate="{first} - {last} di {totalRecords}"
            @page="onDistribuzioneIncanalarePage"
          >
            <template #header>
              <span class="font-semibold">Ticket che verranno spostati</span>
            </template>
            <template #empty>
              <span class="text-color-secondary">Nessun ticket da spostare.</span>
            </template>
            <Column field="id_ticket" header="#" style="width: 90px" />
            <Column field="title" header="Titolo" />
            <Column field="area_origine" header="Da" style="min-width: 160px" />
            <Column field="area_destinazione" header="Verso" style="min-width: 180px" />
            <Column header="Aperto il" style="min-width: 160px">
              <template #body="{ data }">
                {{ formatDateTime(data.datetime) }}
              </template>
            </Column>
          </DataTable>

          <DataTable
            :value="distribuzione.in_coda?.data || []"
            dataKey="id_ticket"
            showGridlines
            class="mt-4 statistiche-agenti-table"
            paginator
            lazy
            :rows="distribuzionePagine.coda.perPage"
            :totalRecords="distribuzione.in_coda?.pagination?.total || 0"
            :first="((distribuzione.in_coda?.pagination?.current_page || 1) - 1) * distribuzionePagine.coda.perPage"
            :loading="loadingDistribuzioneTabelle"
            :rowsPerPageOptions="[15, 25, 50]"
            paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
            currentPageReportTemplate="{first} - {last} di {totalRecords}"
            @page="onDistribuzioneCodaPage"
          >
            <template #header>
              <span class="font-semibold">Ticket aperti in attesa di assegnazione</span>
            </template>
            <template #empty>
              <span class="text-color-secondary">Nessun ticket in coda.</span>
            </template>
            <Column field="id_ticket" header="#" style="width: 90px" />
            <Column field="title" header="Titolo" />
            <Column header="Aperto il" style="min-width: 160px">
              <template #body="{ data }">
                {{ formatDateTime(data.datetime) }}
              </template>
            </Column>
          </DataTable>


          <DataTable
            :value="distribuzione.ultime_assegnazioni?.data || []"
            showGridlines
            class="mt-4 statistiche-agenti-table"
            paginator
            lazy
            :rows="distribuzionePagine.assegnazioni.perPage"
            :totalRecords="distribuzione.ultime_assegnazioni?.pagination?.total || 0"
            :first="((distribuzione.ultime_assegnazioni?.pagination?.current_page || 1) - 1) * distribuzionePagine.assegnazioni.perPage"
            :loading="loadingDistribuzioneTabelle"
            :rowsPerPageOptions="[15, 25, 50]"
            paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
            currentPageReportTemplate="{first} - {last} di {totalRecords}"
            @page="onDistribuzioneAssegnazioniPage"
          >
            <template #header>
              <span class="font-semibold">Ultime assegnazioni automatiche</span>
            </template>
            <template #empty>
              <span class="text-color-secondary">Nessuna assegnazione automatica registrata.</span>
            </template>
            <Column field="id_ticket" header="#" style="width: 90px" />
            <Column field="title" header="Titolo" />
            <Column header="Operatore" style="min-width: 200px">
              <template #body="{ data }">
                {{ `${data.nome || ''} ${data.cognome || ''}`.trim() }}
              </template>
            </Column>
            <Column header="Quando" style="min-width: 160px">
              <template #body="{ data }">
                {{ formatDateTime(data.datetime) }}
              </template>
            </Column>
          </DataTable>
        </template>
      </div>

      <div v-show="searchMode === 'area'" v-if="areas.length > 0" class="tab-pills-section">
        <div class="tab-pills-grid">
          <button
            v-for="(area, index) in areas"
            :key="area.id_ticket_area || area.id"
            type="button"
            class="tab-pill"
            :class="{ active: activeTabIndex === index }"
            @click="onAreaSelect(index)"
          >
            <span class="tab-pill-label">{{ area.area || area.code || `Area ${area.id_ticket_area || area.id}` }}</span>
          </button>
        </div>
      </div>
      <p v-show="searchMode === 'area'" v-else class="filter-empty">Nessuna area disponibile.</p>

      <div v-show="searchMode === 'category'" v-if="categories.length > 0" class="tab-pills-section">
        <div class="tab-pills-grid">
          <button
            type="button"
            class="tab-pill"
            :class="{ active: activeCategoryIndex === -1 }"
            @click="onCategorySelect(-1)"
          >
            <span class="tab-pill-label">Tutte</span>
          </button>
          <button
            v-for="(cat, index) in categories"
            :key="cat.id"
            type="button"
            class="tab-pill"
            :class="{ active: activeCategoryIndex === index }"
            @click="onCategorySelect(index)"
          >
            <span class="tab-pill-label">{{ cat.motivation }}</span>
          </button>
        </div>
      </div>
      <p v-show="searchMode === 'category'" v-else-if="categories.length === 0" class="filter-empty">Nessuna categoria disponibile.</p>

      <div v-if="!searchMode" class="tab-content tab-content-placeholder">
        <p class="text-color-secondary">Seleziona una modalità di ricerca per iniziare.</p>
      </div>
      <div v-else-if="isAreaOrCategorySearchMode && searchMode && !hasActiveFilter" class="tab-content tab-content-placeholder">
        <p class="text-color-secondary">{{ searchMode === 'area' ? 'Seleziona un\'area.' : 'Seleziona una categoria.' }}</p>
      </div>
      <div v-else-if="isAreaOrCategorySearchMode" class="tab-content">
        <div v-if="loadingAndamento" class="content-loading">
          <ProgressSpinner style="width: 50px; height: 50px" strokeWidth="4" />
        </div>
        <div v-else-if="andamento" class="andamento-grid">
          <div
            v-for="item in andamentoItems"
            :key="item.key"
            class="andamento-card"
            :class="{ active: selectedStatusKey === item.key }"
            role="button"
            tabindex="0"
            @click="onStatusClick(item)"
          >
            <span class="andamento-label">{{ item.label }}</span>
            <span class="andamento-value">{{ andamento[item.key] }}</span>
          </div>
        </div>
        <p v-else class="text-color-secondary">Nessun dato disponibile.</p>

        <div v-if="selectedStatusKey" class="detail-section mt-4">
          <h4 class="detail-title">{{ getStatusLabel(selectedStatusKey) }} – Dettaglio ticket</h4>
          <div v-if="loadingDetail" class="content-loading">
            <ProgressSpinner style="width: 50px; height: 50px" strokeWidth="4" />
          </div>
          <template v-else>
            <DataTable
              :value="detailTickets"
              dataKey="id_ticket"
              tableStyle="min-width: 700px"
              responsiveLayout="scroll"
              stripedRows
              showGridlines
              paginator
              :rows="detailPerPage"
              :totalRecords="pagination?.total || 0"
              :first="(detailCurrentPage - 1) * detailPerPage"
              :lazy="true"
              :sortField="detailSortField"
              :sortOrder="detailSortOrder"
              @page="onDetailPageChange"
              @sort="onDetailSort"
              :rowsPerPageOptions="[5, 15, 25, 50]"
              emptyMessage="Nessun ticket"
              class="p-datatable-sm info-ticket-table"
              paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
              currentPageReportTemplate="{first} - {last} di {totalRecords}"
            >
              <Column field="id_ticket" header="ID" sortable :style="{ width: '90px' }" />
              <Column field="datetime" header="Data" sortable :style="{ width: '160px' }">
                <template #body="{ data }">{{ formatDate(data.datetime) }}</template>
              </Column>
              <Column field="title" header="Titolo" :style="{ minWidth: '180px' }" />
              <Column field="creator" header="Creatore" :style="{ minWidth: '140px' }" />
              <Column field="status" header="Stato" sortable :style="{ width: '120px' }">
                <template #body="{ data }">
                  <span
                    class="status-badge"
                    :style="{
                      color: data.color || '#495057',
                      backgroundColor: data.color ? `${data.color}22` : 'var(--p-surface-200)',
                    }"
                  >
                    {{ data.status }}
                  </span>
                </template>
              </Column>
              <Column field="excerpt" header="Ultimo messaggio" :style="{ minWidth: '200px' }">
                <template #body="{ data }">{{ truncate(data.excerpt, 50) }}</template>
              </Column>
              <Column header="Info" :style="{ width: '90px' }">
                <template #body="{ data }">
                  <Button
                    icon="pi pi-info-circle"
                    class="p-button-rounded p-button-text p-button-sm"
                    v-tooltip.top="'Dettaglio ticket'"
                    @click="openTicketInfo(data.id_ticket)"
                  />
                </template>
              </Column>
            </DataTable>
          </template>
        </div>
      </div>
    </template>
    <p v-else class="text-color-secondary">Nessun dato disponibile. Carica aree e categorie.</p>

    <Dialog
      v-model:visible="dialogCategorieTickets"
      :header="categorieTicketsDialogHeader"
      :style="{ width: '96vw', maxWidth: '1420px' }"
      :breakpoints="{ '960px': '98vw', '640px': '100vw' }"
      :modal="true"
      :closable="true"
      class="categorie-tickets-dialog"
      @hide="resetCategorieTicketsDialog"
    >
      <div class="categorie-tickets-dialog-content">
        <div v-if="categorieTicketsFiltri" class="categorie-tickets-summary">
          <span>
            Periodo:
            {{ formatStatsDate(categorieTicketsFiltri.periodo?.data_da) }}
            -
            {{ formatStatsDate(categorieTicketsFiltri.periodo?.data_a) }}
          </span>
          <span v-if="categorieTicketsPagination?.total != null">
            {{ categorieTicketsPagination.total }} ticket trovati
          </span>
        </div>

        <div class="categorie-tickets-filters">
          <label for="categorieTicketsStatusFilter">Stato</label>
          <Dropdown
            id="categorieTicketsStatusFilter"
            v-model="categorieTicketsStatusFilter"
            :options="categorieTicketsStatusOptions"
            optionLabel="label"
            optionValue="value"
            placeholder="Tutti gli stati"
            showClear
            class="categorie-tickets-status-filter"
            @change="onCategorieTicketsStatusChange"
          />
        </div>

        <div v-if="loadingCategorieTickets" class="content-loading">
          <ProgressSpinner style="width: 50px; height: 50px" strokeWidth="4" />
        </div>

        <div v-else-if="categorieTicketsError" class="response-box response-box--error">
          <p class="feedback-message">{{ categorieTicketsError }}</p>
        </div>

        <DataTable
          v-else
          :value="categorieTickets"
          dataKey="id_ticket"
          tableStyle="width: 100%"
          stripedRows
          showGridlines
          paginator
          :rows="categorieTicketsPerPage"
          :totalRecords="categorieTicketsPagination?.total || 0"
          :first="((categorieTicketsPagination?.current_page || 1) - 1) * categorieTicketsPerPage"
          :lazy="true"
          :loading="loadingCategorieTickets"
          :sortField="categorieTicketsSortField"
          :sortOrder="categorieTicketsSortOrder"
          :rowsPerPageOptions="[5, 15, 25, 50]"
          emptyMessage="Nessun ticket trovato"
          class="p-datatable-sm info-ticket-table categorie-tickets-table"
          paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
          currentPageReportTemplate="{first} - {last} di {totalRecords}"
          @page="onCategorieTicketsPageChange"
          @sort="onCategorieTicketsSort"
        >
          <Column field="id_ticket" header="ID" sortable :style="{ width: '90px' }" />
          <Column field="datetime" header="Data" sortable :style="{ width: '160px' }">
            <template #body="{ data }">{{ formatDate(data.datetime) }}</template>
          </Column>
          <Column field="title" header="Titolo" :style="{ minWidth: '220px' }" />
          <Column field="creator" header="Creatore" :style="{ width: '150px' }" />
          <Column field="area" header="Area" :style="{ width: '130px' }" />
          <Column field="status" header="Stato" sortable :style="{ width: '120px' }">
            <template #body="{ data }">
              <span
                class="status-badge"
                :style="{
                  color: data.color || '#495057',
                  backgroundColor: data.color ? `${data.color}22` : 'var(--p-surface-200)',
                }"
              >
                {{ data.status }}
              </span>
            </template>
          </Column>
          <Column field="excerpt" header="Ultimo messaggio">
            <template #body="{ data }">{{ truncate(data.excerpt, 80) }}</template>
          </Column>
          <Column header="Info" :style="{ width: '90px' }">
            <template #body="{ data }">
              <Button
                icon="pi pi-info-circle"
                class="p-button-rounded p-button-text p-button-sm"
                v-tooltip.top="'Dettaglio ticket'"
                @click="openTicketInfo(data.id_ticket)"
              />
            </template>
          </Column>
        </DataTable>
      </div>
      <template #footer>
        <Button label="Chiudi" icon="pi pi-times" @click="dialogCategorieTickets = false" />
      </template>
    </Dialog>

    <Dialog
      v-model:visible="dialogTicketInfo"
      header="Dettaglio ticket"
      :style="{ width: '50rem' }"
      :breakpoints="{ '960px': '90vw', '640px': '95vw' }"
      :modal="true"
      :closable="true"
      class="ticket-info-dialog"
      @hide="ticketInfo = null"
    >
      <Loader v-if="loadingTicketInfo" />
      <div v-else-if="ticketInfo" class="ticket-info-content">
        <div class="ticket-info-header">
          <div class="ticket-info-row">
            <span class="label">#{{ ticketInfo.id_ticket }}</span>
            <span class="status-badge" :style="{ color: ticketInfo.color || '#495057', backgroundColor: ticketInfo.color ? `${ticketInfo.color}22` : 'var(--p-surface-200)' }">
              {{ ticketInfo.status }}
            </span>
          </div>
          <h4 class="ticket-title">{{ ticketInfo.title }}</h4>
          <div class="ticket-meta">
            <span>{{ formatDate(ticketInfo.datetime) }}</span>
            <span>•</span>
            <span>{{ ticketInfo.area || ticketInfo.areaCode }}</span>
            <span>•</span>
            <span>{{ ticketInfo.creator }} ({{ ticketInfo.creator_type }})</span>
          </div>
          <p v-if="ticketInfo.contestazione" class="ticket-contestazione">{{ ticketInfo.contestazione }}</p>
        </div>
        <Accordion v-if="hasMessages" :multiple="true">
          <AccordionTab v-for="(msgs, date) in ticketInfo.messages" :key="date" :header="formatDateShort(date)">
            <div v-for="msg in msgs" :key="msg.id_ticket_message" class="ticket-message">
              <div class="message-meta">
                <strong>{{ msg.sender }}</strong>
                <span>{{ formatDate(msg.datetime) }}</span>
              </div>
              <div class="message-content">{{ msg.content }}</div>
            </div>
          </AccordionTab>
        </Accordion>
        <div v-if="ticketInfo.history_status?.length" class="ticket-section">
          <h5>Storico stati</h5>
          <DataTable :value="ticketInfo.history_status" size="small" showGridlines>
            <Column field="name" header="Utente" />
            <Column field="status" header="Stato" />
            <Column field="datetime" header="Data">
              <template #body="{ data }">{{ formatDate(data.datetime) }}</template>
            </Column>
          </DataTable>
        </div>
        <div v-if="ticketInfo.history_assignment?.length" class="ticket-section">
          <h5>Storico assegnazioni</h5>
          <DataTable :value="ticketInfo.history_assignment" size="small" showGridlines>
            <Column field="user_from" header="Da" />
            <Column field="user_to" header="A" />
            <Column field="area_to" header="Area" />
            <Column field="datetime" header="Data">
              <template #body="{ data }">{{ formatDate(data.datetime) }}</template>
            </Column>
          </DataTable>
        </div>
      </div>
      <p v-else-if="!loadingTicketInfo" class="text-color-secondary">Impossibile caricare il dettaglio.</p>
      <template #footer>
        <Button label="Chiudi" icon="pi pi-times" @click="dialogTicketInfo = false" />
      </template>
    </Dialog>

    <Dialog
      v-model:visible="dialogGestioneTicket"
      :header="gestioneTicketDialogHeader"
      :style="{ width: '56rem' }"
      :breakpoints="{ '960px': '92vw', '640px': '95vw' }"
      :modal="true"
      :closable="true"
      class="gestione-ticket-dialog"
      @hide="selectedGestioneTicket = null"
    >
      <div v-if="selectedGestioneTicket" class="gestione-dialog">
        <div class="gestione-dialog-hero">
          <div class="gestione-dialog-hero-top">
            <span class="gestione-dialog-id">#{{ selectedGestioneTicket.id_ticket }}</span>
            <span
              class="status-badge"
              :class="`status-badge--${selectedGestioneTicket.stato_attuale_code || 'default'}`"
            >
              {{ selectedGestioneTicket.stato_attuale || '-' }}
            </span>
          </div>
          <h4 class="gestione-dialog-titolo">{{ selectedGestioneTicket.titolo || 'Senza titolo' }}</h4>
        </div>

        <section class="gestione-dialog-section">
          <h6 class="gestione-section-title">
            <i class="pi pi-info-circle" aria-hidden="true"></i>
            Informazioni
          </h6>
          <div class="gestione-info-grid">
            <div class="gestione-info-cell">
              <span class="gestione-info-label">Categoria</span>
              <span class="gestione-info-value">{{ selectedGestioneTicket.categoria || '-' }}</span>
            </div>
            <div class="gestione-info-cell">
              <span class="gestione-info-label">Data apertura</span>
              <span class="gestione-info-value">{{ formatDate(selectedGestioneTicket.data_apertura) }}</span>
            </div>
            <div class="gestione-info-cell">
              <span class="gestione-info-label">Tempo nello stato attuale</span>
              <span class="gestione-info-value">{{ selectedGestioneTicket.tempo_nello_stato_attuale || '-' }}</span>
            </div>
            <div class="gestione-info-cell gestione-info-cell--highlight">
              <span class="gestione-info-label">Tempo totale gestione</span>
              <span class="gestione-info-value gestione-info-value--large">
                {{ selectedGestioneTicket.tempo_totale_gestione || '-' }}
              </span>
            </div>
          </div>
        </section>

        <section v-if="selectedGestioneTicket.riepilogo_tempi" class="gestione-dialog-section">
          <h6 class="gestione-section-title">
            <i class="pi pi-clock" aria-hidden="true"></i>
            Tempi per stato
          </h6>
          <div class="gestione-stat-cards">
            <div class="gestione-stat-card gestione-stat-card--opened">
              <span class="gestione-stat-dot" aria-hidden="true"></span>
              <div class="gestione-stat-content">
                <span class="gestione-stat-label">In aperto</span>
                <span class="gestione-stat-value">
                  {{ formatTempoStat(selectedGestioneTicket.riepilogo_tempi.tempo_in_aperto) }}
                </span>
              </div>
            </div>
            <div class="gestione-stat-card gestione-stat-card--in-progress">
              <span class="gestione-stat-dot" aria-hidden="true"></span>
              <div class="gestione-stat-content">
                <span class="gestione-stat-label">In lavorazione</span>
                <span class="gestione-stat-value">
                  {{ formatTempoStat(selectedGestioneTicket.riepilogo_tempi.tempo_in_lavorazione) }}
                </span>
              </div>
            </div>
          </div>
        </section>

        <section
          v-if="getRiepilogoTransizioniItems(selectedGestioneTicket.riepilogo_tempi).length > 0"
          class="gestione-dialog-section"
        >
          <h6 class="gestione-section-title">
            <i class="pi pi-arrow-right-arrow-left" aria-hidden="true"></i>
            Transizioni di stato
          </h6>
          <div class="gestione-transizioni-grid">
            <div
              v-for="item in getRiepilogoTransizioniItems(selectedGestioneTicket.riepilogo_tempi)"
              :key="item.key"
              class="gestione-transizione-card"
            >
              <div class="gestione-transizione-route">
                <span>{{ item.labelDa }}</span>
                <i class="pi pi-arrow-right" aria-hidden="true"></i>
                <span>{{ item.labelA }}</span>
              </div>
              <div class="gestione-transizione-durata">{{ item.data.durata || '-' }}</div>
              <div v-if="item.data.gestito_da" class="gestione-transizione-gestore">
                <i class="pi pi-user" aria-hidden="true"></i>
                {{ item.data.gestito_da }}
              </div>
            </div>
          </div>
        </section>

        <section
          v-if="getPassaggiStatoTicket(selectedGestioneTicket).length > 0"
          class="gestione-dialog-section gestione-dialog-section--last"
        >
          <h6 class="gestione-section-title">
            <i class="pi pi-list" aria-hidden="true"></i>
            Cronologia passaggi
          </h6>
          <DataTable
            :value="getPassaggiStatoTicket(selectedGestioneTicket)"
            size="small"
            showGridlines
            scrollable
            scrollHeight="220px"
            class="gestione-passaggi-table"
          >
            <Column header="Da">
              <template #body="{ data: row }">{{ row.stato_da }}</template>
            </Column>
            <Column header="A">
              <template #body="{ data: row }">{{ row.stato_a }}</template>
            </Column>
            <Column header="Durata">
              <template #body="{ data: row }">
                {{ row.durata_nello_stato_precedente || row.durata || '-' }}
              </template>
            </Column>
            <Column header="Gestito da">
              <template #body="{ data: row }">{{ row.gestito_da || '-' }}</template>
            </Column>
            <Column header="Data cambio">
              <template #body="{ data: row }">{{ formatDate(row.data_cambio) }}</template>
            </Column>
          </DataTable>
        </section>
      </div>

      <template #footer>
        <div class="gestione-dialog-footer">
          <Button
            label="Dettaglio ticket"
            icon="pi pi-external-link"
            class="p-button-text"
            :disabled="!selectedGestioneTicket?.id_ticket"
            @click="openTicketInfoFromGestione"
          />
          <Button label="Chiudi" icon="pi pi-times" @click="dialogGestioneTicket = false" />
        </div>
      </template>
    </Dialog>

    <Dialog v-model:visible="dialogStatistiche" modal header="Statistiche" :style="{ width: '75%' }" @hide="hideDialogStats()">
      <div class="flex flex-wrap justify-content-around">
        <!-- Ciclo v-for sugli utenti -->
        <div v-for="(utente, i) in statistiche.generale" :key="i" class="m-3 card-stat-utente">
          
          <Card class="h-full shadow-md">
            <!-- Intestazione della Card con Icona e Nome Utente -->
            <template #title>
              <div class="flex align-items-center gap-2 mb-3 h-3rem">
                <i class="pi pi-user text-primary text-2xl mr-2"></i>
                <span class="font-semibold">{{ utente.user_name }}</span>
              </div>
            </template>
            
            <!-- Corpo della Card con i dati statistici -->
            <template #content>
              <div class="flex flex-column gap-2 text-sm text-600">
                
                <div class="flex justify-content-between border-bottom-1 surface-border pb-2">
                  <span>Aperti:</span>
                  <span class="font-bold text-900">{{ utente.aperto }}</span>
                </div>
                
                <div class="flex justify-content-between border-bottom-1 surface-border pb-2">
                  <span>In Lavorazione:</span>
                  <span class="font-bold text-900">{{ utente.in_lavorazione }}</span>
                </div>
                
                <div class="flex justify-content-between border-bottom-1 surface-border pb-2">
                  <span>Chiusi Totali:</span>
                  <span class="font-bold text-900">{{ utente.totale_chiuso }}</span>
                </div>
                
                <!-- <div class="flex justify-content-between pt-1 mt-1 border-top-1 surface-border">
                  <span class="font-semibold text-900">Totale Complessivo:</span>
                  <span class="font-bold text-primary">{{ utente.totale_complessivo }}</span>
                </div> -->
                
              </div>
            </template>
          </Card>
          
        </div>
      </div>
      <div class="flex">

        <DataTable :value="chiusi_month" class="col-12 md:col-6">
          <template #header>Ticket chiusi del mese <Calendar v-model="this_month" view="month" dateFormat="mm/yy" :maxDate="max_month" :manualInput="false" @update:modelValue="changeDateStats('month')"/></template>
          <template #empty>Non ci sono ticket chiusi del mese {{ moment(this_month_formatted).format('MM/YYYY') }}</template>
          <Column v-for="col of colonneChiusi" :key="col.field" :field="col.field" :header="col.header"></Column>
        </DataTable>
        <DataTable :value="chiusi_day" class="col-12 md:col-6">
          <template #header>Ticket chiusi del giorno <Calendar v-model="this_day" dateFormat="dd/mm/yy" :maxDate="max_day" :manualInput="false" @update:modelValue="changeDateStats('day')"/></template>
          <template #empty>Non ci sono ticket chiusi del giorno {{ moment(this_day_formatted).format('DD/MM/YYYY') }}</template>
          <Column v-for="col of colonneChiusi" :key="col.field" :field="col.field" :header="col.header"></Column>
        </DataTable>
      </div>
    </Dialog>
  </div>
</template>

<script>
import TicketService from '@/services/ask-ticket/ticket-service'
import UtilityService from '@/services/ask-management/utility-service'
import Loader from '@/components/Loader.vue'
import moment from 'moment';

const ANDAMENTO_ITEMS = [
  { key: 'totale', label: 'Totale', statusCode: 'all' },
  { key: 'aperti', label: 'Aperti', statusCode: 'opened' },
  { key: 'chiusi', label: 'Chiusi', statusCode: 'closed' },
]

export default {
  name: 'InfoTicket',
  components: {
    Loader,
  },
  data() {
    return {
      moment,
      areas: [],
      categories: [],
      loading: false,
      loadingAndamento: false,
      loadingDetail: false,
      searchMode: null,
      loadingDistribuzione: false,
      loadingDistribuzioneTabelle: false,
      distribuzione: null,
      distribuzioneError: '',
      distribuzionePagine: {
        coda: { page: 1, perPage: 15 },
        incanalare: { page: 1, perPage: 15 },
        assegnazioni: { page: 1, perPage: 15 },
      },
      activeTabIndex: -1,
      activeCategoryIndex: -1,
      andamento: null,
      selectedStatusKey: null,
      detailTickets: [],
      pagination: null,
      detailCurrentPage: 1,
      detailPerPage: 15,
      detailSortField: 'datetime',
      detailSortOrder: -1,
      dialogTicketInfo: false,
      loadingTicketInfo: false,
      ticketInfo: null,
      ticketService: null,
      andamentoItems: ANDAMENTO_ITEMS,
      dialogStatistiche: false,
      max_day: null,
      max_month: null,
      this_day: null,
      this_month: null,
      this_day_formatted: null,
      this_month_formatted: null,
      statistiche: null,
      chiusi_month: null,
      chiusi_day: null,
      colonneChiusi: [],
      loadingStatisticheAgenti: false,
      loadingAgentiStats: false,
      agentiStats: [],
      selectedAgenteStats: null,
      statisticheAgentiDataDa: null,
      statisticheAgentiDataA: null,
      maxStatisticheAgentiDate: new Date(),
      statisticheAgenti: null,
      statisticheAgentiError: null,
      colonneStatisticheAgenti: [],
      statisticheAgentiCategorie: [],
      colonneStatisticheAgentiCategorie: [],
      selectedCategoriaStatsFilter: null,
      filtroCategoriaStats: '',
      gestioneTicketAgenti: [],
      colonneGestioneTicketAgenti: [],
      gestioneTicketPagination: null,
      gestioneTicketCurrentPage: 1,
      gestioneTicketPerPage: 15,
      loadingGestioneTicket: false,
      dialogGestioneTicket: false,
      selectedGestioneTicket: null,
      utilityService: null,
      loadingStatisticheCategorie: false,
      statisticheCategorieDataDa: null,
      statisticheCategorieDataA: null,
      maxStatisticheCategorieDate: new Date(),
      statisticheCategorie: null,
      statisticheCategorieError: null,
      dialogCategorieTickets: false,
      selectedCategoriaStatsRow: null,
      categorieTickets: [],
      categorieTicketsPagination: null,
      categorieTicketsFiltri: null,
      categorieTicketsError: null,
      loadingCategorieTickets: false,
      categorieTicketsStatusFilter: null,
      categorieTicketsPage: 1,
      categorieTicketsPerPage: 15,
      categorieTicketsSortField: 'datetime',
      categorieTicketsSortOrder: -1,
      categorieTicketsStatusOptions: [
        { label: 'Tutti', value: null },
        { label: 'Aperti', value: 'opened' },
        { label: 'In lavorazione', value: 'in-progress' },
        { label: 'Chiusi', value: 'closed' },
      ],
      loadingStatisticheTrasferimenti: false,
      statisticheTrasferimentiDataDa: null,
      statisticheTrasferimentiDataA: null,
      maxStatisticheTrasferimentiDate: new Date(),
      statisticheTrasferimenti: null,
      statisticheTrasferimentiError: null,
    }
  },
  computed: {
    isAreaOrCategorySearchMode() {
      return this.searchMode === 'area' || this.searchMode === 'category'
    },
    selectedArea() {
      return this.activeTabIndex >= 0 ? (this.areas[this.activeTabIndex] || null) : null
    },
    hasActiveFilter() {
      if (this.searchMode === 'area') return this.activeTabIndex >= 0
      if (this.searchMode === 'category') return true
      return false
    },
    selectedCategory() {
      return this.activeCategoryIndex >= 0 && this.categories[this.activeCategoryIndex]
        ? this.categories[this.activeCategoryIndex]
        : null
    },
    hasMessages() {
      return this.ticketInfo?.messages && Object.keys(this.ticketInfo.messages).length > 0
    },
    statisticheAgentiRows() {
      if (!this.statisticheAgenti) {
        return []
      }

      return [{
        aperti: this.statisticheAgenti.aperti,
        in_lavorazione: this.statisticheAgenti.in_lavorazione,
        chiusi: this.statisticheAgenti.chiusi,
        totale: this.statisticheAgenti.totale,
      }]
    },
    categorieStatsFilterOptions() {
      return this.statisticheAgentiCategorie.map((row) => ({
        label: row.categoria,
        value: row.id_category_motivation,
      }))
    },
    statisticheAgentiCategorieFiltrate() {
      return this.filterStatisticheAgentiByCategoria(this.statisticheAgentiCategorie, {
        getCategoryId: (row) => row.id_category_motivation,
        getCategoryName: (row) => row.categoria,
      })
    },
    gestioneTicketAgentiFiltrati() {
      return this.filterStatisticheAgentiByCategoria(this.gestioneTicketAgenti, {
        getCategoryId: (row) => row.id_category_motivation,
        getCategoryName: (row) => row.categoria,
      })
    },
    gestioneTicketDialogHeader() {
      if (!this.selectedGestioneTicket?.id_ticket) {
        return 'Dettaglio gestione ticket'
      }

      return `Dettaglio gestione ticket #${this.selectedGestioneTicket.id_ticket}`
    },
    statisticheCategorieRows() {
      return this.mapStatisticheCategorieRows(this.statisticheCategorie?.categorie)
    },
    statisticheCategorieRiepilogoRows() {
      if (!this.statisticheCategorie?.riepilogo) {
        return []
      }

      const riepilogo = this.statisticheCategorie.riepilogo
      return this.mapStatisticheCategorieRows([{
        id_category_motivation: 'riepilogo',
        categoria: riepilogo.periodo?.categoria || 'Totale',
        periodo: riepilogo.periodo,
        mese_precedente: riepilogo.mese_precedente,
        confronto_mese_precedente: riepilogo.confronto_mese_precedente,
      }])
    },
    statisticheCategorieRiepilogoRow() {
      return this.statisticheCategorieRiepilogoRows[0] || null
    },
    categorieTicketsDialogHeader() {
      const categoria = this.selectedCategoriaStatsRow?.categoria
        || this.categorieTicketsFiltri?.categoria
        || 'Categoria'
      return `Ticket categoria: ${categoria}`
    },
    statisticheTrasferimentiRows() {
      return this.mapStatisticheTrasferimentiRows(this.statisticheTrasferimenti?.reparti)
    },
    statisticheTrasferimentiRiepilogo() {
      const riepilogo = this.statisticheTrasferimenti?.riepilogo
      if (!riepilogo?.periodo) {
        return null
      }

      return {
        ricevuti: riepilogo.periodo.ricevuti || {},
        effettuati: riepilogo.periodo.effettuati || {},
        confronto_ricevuti: riepilogo.confronto_mese_precedente?.ricevuti?.totale || { valore: 0, percentuale: 0 },
        confronto_effettuati: riepilogo.confronto_mese_precedente?.effettuati?.totale || { valore: 0, percentuale: 0 },
      }
    },
  },
  watch: {
    searchMode(mode) {
      if (mode === 'category') this.loadAndamento()
    },
    selectedArea() {
      if (this.searchMode === 'area') this.loadAndamento()
      this.selectedStatusKey = null
      this.detailTickets = []
      this.pagination = null
      this.detailSortField = 'datetime'
      this.detailSortOrder = -1
    },
    selectedCategory() {
      if (this.searchMode === 'category') this.loadAndamento()
      if (this.selectedStatusKey) {
        this.detailCurrentPage = 1
        this.loadDetailTickets(1)
      } else {
        this.selectedStatusKey = null
        this.detailTickets = []
        this.pagination = null
      }
    },
  },
  async mounted() {
    this.resetDateStats()
    this.$eventBus.emit('page-change', {
      target: this,
      currentRoute: this.$router.currentRoute.value,
    })
    this.ticketService = new TicketService(this.$http, this.$eventBus)
    this.utilityService = new UtilityService(this.$http, this.$eventBus)
    await Promise.all([this.loadAreas(), this.loadCategories()])
  },
  methods: {
    async loadAreas() {
      this.loading = true
      try {
        const { items } = await this.ticketService.getInfoAreaTicket()
        this.areas = Array.isArray(items) ? items : []
      } catch (err) {
        this.areas = []
      } finally {
        this.loading = false
      }
    },
    async loadCategories() {
      try {
        const { items } = await this.ticketService.getInfoCategoryTicket()
        this.categories = Array.isArray(items) ? items : []
      } catch (err) {
        this.categories = []
      }
    },
    onSearchModeSelect(mode) {
      this.searchMode = mode
      this.activeTabIndex = -1
      this.activeCategoryIndex = -1
      this.andamento = null
      this.selectedStatusKey = null
      this.detailTickets = []
      this.pagination = null

      if (mode === 'agenti') {
        this.resetStatisticheAgentiResult()
        this.selectedAgenteStats = null
        this.resetStatisticheAgentiDates()
        this.loadAgentiForStats()
      }

      if (mode === 'categorie') {
        this.resetStatisticheCategorieResult()
        this.resetStatisticheCategorieDates()
      }

      if (mode === 'trasferimenti') {
        this.resetStatisticheTrasferimentiResult()
        this.resetStatisticheTrasferimentiDates()
      }

      if (mode === 'distribuzione') {
        this.loadDistribuzione()
      }
    },
    onAreaSelect(index) {
      this.activeTabIndex = index
      this.activeCategoryIndex = -1
    },
    onCategorySelect(index) {
      this.activeCategoryIndex = index
      if (index >= 0) this.activeTabIndex = -1
      if (this.searchMode === 'category') this.loadAndamento()
    },
    async loadAndamento() {
      const params = {}
      if (this.searchMode === 'area') {
        const area = this.selectedArea
        if (!area) return
        if (area.id_ticket_area) params.id_ticket_area = area.id_ticket_area
        else if (area.id) params.id_ticket_area = area.id
        else if (area.code) params.area_code = area.code
      } else if (this.searchMode === 'category') {
        if (this.activeCategoryIndex >= 0 && this.selectedCategory) {
          params.id_category_motivation = this.selectedCategory.id
        } else {
          params.id_category_motivation = 'all'
        }
      } else return

      this.loadingAndamento = true
      this.andamento = null
      try {
        const { success, data } = await this.ticketService.getInfoAndamentoTicket(params)
        this.andamento = success ? data : null
      } catch (err) {
        this.andamento = null
      } finally {
        this.loadingAndamento = false
      }
    },
    onStatusClick(item) {
      this.selectedStatusKey = item.key
      this.detailCurrentPage = 1
      this.detailSortField = 'datetime'
      this.detailSortOrder = -1
      this.loadDetailTickets()
    },
    async loadDetailTickets(page = 1) {
      const hasFilter = this.searchMode === 'area' ? this.selectedArea : (this.searchMode === 'category')
      if (!hasFilter || !this.selectedStatusKey) return
      const item = this.andamentoItems.find((i) => i.key === this.selectedStatusKey)
      if (!item?.statusCode) return
      this.loadingDetail = true
      this.detailTickets = []
      try {
        const params = {
          status_code: item.statusCode,
          page,
          per_page: this.detailPerPage,
          sort_field: this.detailSortField,
          sort_order: this.detailSortOrder === 1 ? 'asc' : 'desc',
        }
        if (this.searchMode === 'area' && this.selectedArea) {
          if (this.selectedArea.id_ticket_area) params.id_ticket_area = this.selectedArea.id_ticket_area
          else if (this.selectedArea.id) params.id_ticket_area = this.selectedArea.id
          else if (this.selectedArea.code) params.area_code = this.selectedArea.code
        } else if (this.searchMode === 'category') {
          if (this.activeCategoryIndex >= 0 && this.selectedCategory) {
            params.id_category_motivation = this.selectedCategory.id
          } else {
            params.id_category_motivation = 'all'
          }
        }
        const { success, data, pagination } = await this.ticketService.getInfoDetailTickets(params)
        this.detailTickets = success ? data : []
        this.pagination = pagination
      } catch (err) {
        this.detailTickets = []
        this.pagination = null
      } finally {
        this.loadingDetail = false
      }
    },
    onDetailPageChange(event) {
      this.detailCurrentPage = event.page + 1
      this.detailPerPage = event.rows
      this.loadDetailTickets(this.detailCurrentPage)
    },
    onDetailSort(event) {
      this.detailSortField = event.sortField || 'datetime'
      this.detailSortOrder = event.sortOrder ?? -1
      this.detailCurrentPage = 1
      this.loadDetailTickets(1)
    },
    getStatusLabel(key) {
      return this.andamentoItems.find((i) => i.key === key)?.label || key
    },
    formatDate(val) {
      if (!val) return ''
      const d = new Date(val)
      return isNaN(d.getTime()) ? val : d.toLocaleString('it-IT')
    },
    truncate(str, len) {
      if (!str) return ''
      return str.length <= len ? str : str.slice(0, len) + '...'
    },
    async openTicketInfo(idTicket) {
      if (!idTicket) return
      this.dialogTicketInfo = true
      this.loadingTicketInfo = true
      this.ticketInfo = null
      try {
        const { success, data } = await this.ticketService.getInfoTicketDetail(idTicket)
        this.ticketInfo = success ? data : null
      } catch (err) {
        this.ticketInfo = null
      } finally {
        this.loadingTicketInfo = false
      }
    },
    formatDateShort(val) {
      if (!val) return ''
      const d = new Date(val)
      return isNaN(d.getTime()) ? val : d.toLocaleDateString('it-IT', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })
    },
    changeDateStats(type_date){
      let format = type_date == 'day' ? 'YYYY-MM-DD' : 'YYYY-MM'
      this['this_'+type_date+'_formatted'] = moment(this['this_'+type_date]).format(format)
      this.setStatisticheChiusi([type_date])      
    },
    setStatisticheChiusi(props){
      props.forEach(prop => {
      let prop_in_stats = prop + 's'; 

      let chiaveChiusi = 'chiusi_' + prop;
      let chiaveThisFormatted = 'this_' + prop + '_formatted';
      this[chiaveChiusi] = this.statistiche[prop_in_stats][this[chiaveThisFormatted]]
    });
    },
    async getStatistiche() {
      this.loading = true
      await this.ticketService.getStatistiche()
      .then((res) => {
        this.statistiche = res.items
        this.colonneChiusi = res.items.colonne
        this.setStatisticheChiusi(['day', 'month'])
        this.dialogStatistiche = true
      })
      .finally(()=>{
        this.loading = false
      })
    },
    resetDateStats(){
      this.this_day = new Date()
      this.this_month = new Date()
      this.max_day = new Date()
      this.max_month = new Date()
      this.this_day_formatted = moment().format('YYYY-MM-DD')
      this.this_month_formatted = moment().format('YYYY-MM')
    },
    hideDialogStats(){
      this.dialogStatistiche = false
      this.resetDateStats()
    },
    resetStatisticheAgentiDates() {
      this.statisticheAgentiDataA = new Date()
      this.statisticheAgentiDataDa = moment().subtract(1, 'month').toDate()
      this.maxStatisticheAgentiDate = new Date()
    },
    resetStatisticheAgentiResult() {
      this.statisticheAgenti = null
      this.statisticheAgentiError = null
      this.colonneStatisticheAgenti = []
      this.statisticheAgentiCategorie = []
      this.colonneStatisticheAgentiCategorie = []
      this.selectedCategoriaStatsFilter = null
      this.filtroCategoriaStats = ''
      this.gestioneTicketAgenti = []
      this.colonneGestioneTicketAgenti = []
      this.gestioneTicketPagination = null
      this.gestioneTicketCurrentPage = 1
      this.gestioneTicketPerPage = 15
      this.loadingGestioneTicket = false
      this.selectedGestioneTicket = null
      this.dialogGestioneTicket = false
    },
    applyStatisticheAgentiResponse(res) {
      this.statisticheAgenti = res.data || null
      this.colonneStatisticheAgenti = Array.isArray(res.colonne) ? res.colonne : []
      this.statisticheAgentiCategorie = Array.isArray(res.categorie) ? res.categorie : []
      this.colonneStatisticheAgentiCategorie = Array.isArray(res.colonne_categorie) ? res.colonne_categorie : []
      this.selectedCategoriaStatsFilter = null
      this.filtroCategoriaStats = ''
      this.gestioneTicketAgenti = Array.isArray(res.gestione_ticket) ? res.gestione_ticket : []
      this.colonneGestioneTicketAgenti = Array.isArray(res.colonne_gestione_ticket) ? res.colonne_gestione_ticket : []
      this.gestioneTicketPagination = res.pagination || null
      this.gestioneTicketCurrentPage = res.pagination?.current_page || 1
      if (res.pagination?.per_page) {
        this.gestioneTicketPerPage = res.pagination.per_page
      }
      this.selectedGestioneTicket = null
      this.dialogGestioneTicket = false
    },
    applyGestioneTicketPageResponse(res) {
      this.gestioneTicketAgenti = Array.isArray(res.gestione_ticket) ? res.gestione_ticket : []
      if (Array.isArray(res.colonne_gestione_ticket) && res.colonne_gestione_ticket.length > 0) {
        this.colonneGestioneTicketAgenti = res.colonne_gestione_ticket
      }
      this.gestioneTicketPagination = res.pagination || null
      this.gestioneTicketCurrentPage = res.pagination?.current_page || this.gestioneTicketCurrentPage
      if (res.pagination?.per_page) {
        this.gestioneTicketPerPage = res.pagination.per_page
      }
    },
    onGestioneTicketPageChange(event) {
      this.gestioneTicketCurrentPage = event.page + 1
      this.gestioneTicketPerPage = event.rows
      this.loadGestioneTicketPage(this.gestioneTicketCurrentPage)
    },
    async loadGestioneTicketPage(page = this.gestioneTicketCurrentPage) {
      if (!this.selectedAgenteStats?.id) {
        return
      }

      this.loadingGestioneTicket = true
      this.statisticheAgentiError = null

      try {
        const res = await this.ticketService.getStatisticheAgenti({
          id_agente: this.selectedAgenteStats.id,
          data_da: moment(this.statisticheAgentiDataDa).format('YYYY-MM-DD'),
          data_a: moment(this.statisticheAgentiDataA).format('YYYY-MM-DD'),
          page,
          per_page: this.gestioneTicketPerPage,
        })

        if (res?.success) {
          this.applyGestioneTicketPageResponse(res)
          return
        }

        this.statisticheAgentiError = res?.message || 'Impossibile caricare i ticket gestiti.'
      } catch (err) {
        this.statisticheAgentiError = err?.response?.data?.message || 'Impossibile caricare i ticket gestiti.'
      } finally {
        this.loadingGestioneTicket = false
      }
    },
    onGestioneTicketRowClick(event) {
      if (event?.data) {
        this.openGestioneTicketDialog(event.data)
      }
    },
    openGestioneTicketDialog(ticket) {
      this.selectedGestioneTicket = ticket
      this.dialogGestioneTicket = true
    },
    openTicketInfoFromGestione() {
      const idTicket = this.selectedGestioneTicket?.id_ticket
      if (!idTicket) {
        return
      }

      this.dialogGestioneTicket = false
      this.openTicketInfo(idTicket)
    },
    getGestioneColStyle(field) {
      if (field === 'titolo' || field === 'gestito_da' || field === 'stato_da' || field === 'stato_a') {
        return { minWidth: '180px' }
      }
      if (field === 'id_ticket') {
        return { width: '100px' }
      }
      if (field === 'tempo_nello_stato_attuale' || field === 'durata_media') {
        return { minWidth: '170px' }
      }
      return { minWidth: '140px' }
    },
    filterStatisticheAgentiByCategoria(rows, { getCategoryId, getCategoryName }) {
      let filteredRows = Array.isArray(rows) ? [...rows] : []

      if (this.selectedCategoriaStatsFilter != null) {
        const categoriaSelezionata = this.statisticheAgentiCategorie.find(
          (row) => row.id_category_motivation === this.selectedCategoriaStatsFilter
        )

        filteredRows = filteredRows.filter((row) => {
          const categoryId = getCategoryId(row)
          if (categoryId != null && categoryId !== '') {
            return Number(categoryId) === Number(this.selectedCategoriaStatsFilter)
          }

          const categoryName = getCategoryName(row)
          if (categoriaSelezionata?.categoria && categoryName) {
            return categoryName === categoriaSelezionata.categoria
          }

          return false
        })
      }

      const query = this.filtroCategoriaStats.trim().toLowerCase()
      if (query) {
        filteredRows = filteredRows.filter((row) => {
          const categoryName = getCategoryName(row)
          return String(categoryName || '').toLowerCase().includes(query)
        })
      }

      return filteredRows
    },
    isTempoGestioneInCorso(ticket) {
      return ['opened', 'in-progress'].includes(ticket?.stato_attuale_code)
    },
    formatTempoStat(tempo) {
      if (!tempo) {
        return '-'
      }

      const durata = tempo.durata || '-'
      return tempo.in_corso ? `${durata} (in corso)` : durata
    },
    formatPassaggioStato(passaggio) {
      if (!passaggio) {
        return '-'
      }

      const transizione = `${passaggio.da || passaggio.stato_da} → ${passaggio.a || passaggio.stato_a}`
      const durata = passaggio.durata || '-'
      const gestore = passaggio.gestito_da ? ` · ${passaggio.gestito_da}` : ''
      return `${transizione}: ${durata}${gestore}`
    },
    getPassaggiStatoTicket(ticket) {
      if (Array.isArray(ticket?.passaggi_stato) && ticket.passaggi_stato.length > 0) {
        return ticket.passaggi_stato
      }

      if (Array.isArray(ticket?.transizioni) && ticket.transizioni.length > 0) {
        return ticket.transizioni
      }

      return []
    },
    getRiepilogoTransizioniItems(riepilogo) {
      if (!riepilogo) {
        return []
      }

      return [
        {
          key: 'aperto_a_in_lavorazione',
          labelDa: 'Aperto',
          labelA: 'In lavorazione',
          data: riepilogo.aperto_a_in_lavorazione,
        },
        {
          key: 'in_lavorazione_a_chiuso',
          labelDa: 'In lavorazione',
          labelA: 'Chiuso',
          data: riepilogo.in_lavorazione_a_chiuso,
        },
        {
          key: 'aperto_a_chiuso',
          labelDa: 'Aperto',
          labelA: 'Chiuso',
          data: riepilogo.aperto_a_chiuso,
        },
      ].filter((item) => item.data)
    },
    async loadAgentiForStats() {
      if (this.agentiStats.length > 0) {
        return
      }

      this.loadingAgentiStats = true
      this.statisticheAgentiError = null

      try {
        const { items } = await this.utilityService.getAgenti()
        this.agentiStats = Array.isArray(items) ? items : []

        if (this.agentiStats.length === 0) {
          this.statisticheAgentiError = 'Nessun agente disponibile.'
        }
      } catch (err) {
        this.agentiStats = []
        this.statisticheAgentiError = 'Impossibile caricare l\'elenco agenti.'
      } finally {
        this.loadingAgentiStats = false
      }
    },
    formatStatsDate(value) {
      if (!value) return ''
      return moment(value).format('DD/MM/YYYY')
    },
    formatDateTime(value) {
      if (!value) return ''
      return moment(value).format('DD/MM/YYYY HH:mm')
    },
    assenzaLabel(assenza) {
      if (!assenza) return 'Assente'
      if (assenza.tipo === 'ore' && assenza.ora_inizio && assenza.ora_fine) {
        return `Permesso ${assenza.ora_inizio}–${assenza.ora_fine}`
      }
      const dal = this.formatStatsDate(assenza.data_inizio)
      const al = this.formatStatsDate(assenza.data_fine)
      return dal === al ? `Ferie ${dal}` : `Ferie ${dal} – ${al}`
    },
    aggiornaDistribuzione() {
      this.distribuzionePagine.coda.page = 1
      this.distribuzionePagine.incanalare.page = 1
      this.distribuzionePagine.assegnazioni.page = 1
      this.loadDistribuzione()
    },
    onDistribuzioneCodaPage(event) {
      this.distribuzionePagine.coda.page = event.page + 1
      this.distribuzionePagine.coda.perPage = event.rows
      this.loadDistribuzione()
    },
    onDistribuzioneIncanalarePage(event) {
      this.distribuzionePagine.incanalare.page = event.page + 1
      this.distribuzionePagine.incanalare.perPage = event.rows
      this.loadDistribuzione()
    },
    onDistribuzioneAssegnazioniPage(event) {
      this.distribuzionePagine.assegnazioni.page = event.page + 1
      this.distribuzionePagine.assegnazioni.perPage = event.rows
      this.loadDistribuzione()
    },
    async loadDistribuzione() {
      const primoCaricamento = !this.distribuzione
      if (primoCaricamento) {
        this.loadingDistribuzione = true
      }
      this.loadingDistribuzioneTabelle = true
      this.distribuzioneError = ''
      try {
        const pagine = this.distribuzionePagine
        const res = await this.ticketService.getDistribuzioneAssistenza({
          page_coda: pagine.coda.page,
          per_page_coda: pagine.coda.perPage,
          page_incanalare: pagine.incanalare.page,
          per_page_incanalare: pagine.incanalare.perPage,
          page_assegnazioni: pagine.assegnazioni.page,
          per_page_assegnazioni: pagine.assegnazioni.perPage,
        })
        if (!res?.success) {
          this.distribuzione = null
          this.distribuzioneError = res?.message || 'Impossibile caricare la distribuzione.'
          return
        }
        this.distribuzione = res.data
      } catch (err) {
        this.distribuzione = null
        this.distribuzioneError = 'Impossibile caricare la distribuzione.'
      } finally {
        this.loadingDistribuzione = false
        this.loadingDistribuzioneTabelle = false
      }
    },
    resetStatisticheCategorieDates() {
      this.statisticheCategorieDataA = new Date()
      this.statisticheCategorieDataDa = moment().startOf('month').toDate()
      this.maxStatisticheCategorieDate = new Date()
    },
    resetStatisticheCategorieResult() {
      this.statisticheCategorie = null
      this.statisticheCategorieError = null
      this.resetCategorieTicketsDialog()
    },
    resetStatisticheTrasferimentiDates() {
      this.statisticheTrasferimentiDataA = new Date()
      this.statisticheTrasferimentiDataDa = moment().startOf('month').toDate()
      this.maxStatisticheTrasferimentiDate = new Date()
    },
    resetStatisticheTrasferimentiResult() {
      this.statisticheTrasferimenti = null
      this.statisticheTrasferimentiError = null
    },
    mapStatisticheTrasferimentiRows(reparti = []) {
      if (!Array.isArray(reparti)) {
        return []
      }

      return reparti.map((row) => {
        const periodoRicevuti = row.periodo?.ricevuti || {}
        const periodoEffettuati = row.periodo?.effettuati || {}
        const mesePrecedenteRicevuti = row.mese_precedente?.ricevuti || {}
        const mesePrecedenteEffettuati = row.mese_precedente?.effettuati || {}
        const confrontoRicevuti = row.confronto_mese_precedente?.ricevuti || {}
        const confrontoEffettuati = row.confronto_mese_precedente?.effettuati || {}

        return {
          id_ticket_area: row.id_ticket_area,
          reparto: row.reparto || periodoRicevuti.reparto || periodoEffettuati.reparto || 'Senza reparto',
          area_code: row.area_code || null,
          ricevuti_totale: periodoRicevuti.totale ?? 0,
          ricevuti_percentuale_sul_totale: periodoRicevuti.percentuale_sul_totale ?? 0,
          ricevuti_con_successo: periodoRicevuti.con_successo ?? 0,
          ricevuti_percentuale_con_successo: periodoRicevuti.percentuale_con_successo ?? 0,
          ricevuti_falliti: periodoRicevuti.falliti ?? 0,
          ricevuti_percentuale_falliti: periodoRicevuti.percentuale_falliti ?? 0,
          ricevuti_confronto_totale: confrontoRicevuti.totale || { valore: 0, percentuale: 0 },
          ricevuti_mese_precedente: mesePrecedenteRicevuti.totale ?? 0,
          effettuati_totale: periodoEffettuati.totale ?? 0,
          effettuati_percentuale_sul_totale: periodoEffettuati.percentuale_sul_totale ?? 0,
          effettuati_con_successo: periodoEffettuati.con_successo ?? 0,
          effettuati_percentuale_con_successo: periodoEffettuati.percentuale_con_successo ?? 0,
          effettuati_falliti: periodoEffettuati.falliti ?? 0,
          effettuati_percentuale_falliti: periodoEffettuati.percentuale_falliti ?? 0,
          effettuati_confronto_totale: confrontoEffettuati.totale || { valore: 0, percentuale: 0 },
          effettuati_mese_precedente: mesePrecedenteEffettuati.totale ?? 0,
        }
      })
    },
    getTrasferimentiRowClass(data) {
      return data?.id_ticket_area === 0 ? 'stats-categorie-row--uncategorized' : ''
    },
    resetCategorieTicketsDialog() {
      this.dialogCategorieTickets = false
      this.selectedCategoriaStatsRow = null
      this.categorieTickets = []
      this.categorieTicketsPagination = null
      this.categorieTicketsFiltri = null
      this.categorieTicketsError = null
      this.categorieTicketsStatusFilter = null
      this.categorieTicketsPage = 1
      this.categorieTicketsPerPage = 15
      this.categorieTicketsSortField = 'datetime'
      this.categorieTicketsSortOrder = -1
    },
    onCategoriaStatsRowClick(event) {
      if (event?.data) {
        this.openCategoriaTicketsDialog(event.data)
      }
    },
    openCategoriaTicketsDialog(categoryRow) {
      this.selectedCategoriaStatsRow = categoryRow
      this.categorieTicketsStatusFilter = null
      this.categorieTicketsPage = 1
      this.categorieTicketsSortField = 'datetime'
      this.categorieTicketsSortOrder = -1
      this.dialogCategorieTickets = true
      this.loadCategorieTickets(1)
    },
    onCategorieTicketsStatusChange() {
      this.categorieTicketsPage = 1
      this.loadCategorieTickets(1)
    },
    onCategorieTicketsPageChange(event) {
      this.categorieTicketsPage = event.page + 1
      this.categorieTicketsPerPage = event.rows
      this.loadCategorieTickets(this.categorieTicketsPage)
    },
    onCategorieTicketsSort(event) {
      this.categorieTicketsSortField = event.sortField || 'datetime'
      this.categorieTicketsSortOrder = event.sortOrder ?? -1
      this.categorieTicketsPage = 1
      this.loadCategorieTickets(1)
    },
    async loadCategorieTickets(page = this.categorieTicketsPage) {
      if (!this.selectedCategoriaStatsRow) {
        return
      }

      this.loadingCategorieTickets = true
      this.categorieTicketsError = null

      try {
        const params = {
          data_da: moment(this.statisticheCategorieDataDa).format('YYYY-MM-DD'),
          data_a: moment(this.statisticheCategorieDataA).format('YYYY-MM-DD'),
          id_category_motivation: this.selectedCategoriaStatsRow.id_category_motivation,
          page,
          per_page: this.categorieTicketsPerPage,
          sort_field: this.categorieTicketsSortField,
          sort_order: this.categorieTicketsSortOrder === 1 ? 'asc' : 'desc',
        }

        if (this.categorieTicketsStatusFilter) {
          params.status_code = this.categorieTicketsStatusFilter
        }

        const res = await this.ticketService.getStatisticheCategorieTickets(params)

        if (res?.success) {
          this.categorieTickets = Array.isArray(res.data) ? res.data : []
          this.categorieTicketsPagination = res.pagination || null
          this.categorieTicketsFiltri = res.filtri || null
          this.categorieTicketsPage = res.pagination?.current_page || page
          if (res.pagination?.per_page) {
            this.categorieTicketsPerPage = res.pagination.per_page
          }
          return
        }

        this.categorieTickets = []
        this.categorieTicketsPagination = null
        this.categorieTicketsFiltri = null
        this.categorieTicketsError = res?.message || 'Impossibile caricare i ticket della categoria.'
      } catch (err) {
        this.categorieTickets = []
        this.categorieTicketsPagination = null
        this.categorieTicketsFiltri = null
        this.categorieTicketsError = err?.response?.data?.message || 'Impossibile caricare i ticket della categoria.'
      } finally {
        this.loadingCategorieTickets = false
      }
    },
    mapStatisticheCategorieRows(rows) {
      if (!Array.isArray(rows)) {
        return []
      }

      return rows.map((row) => {
        const periodo = row.periodo || {}
        const mesePrecedente = row.mese_precedente || {}
        const confronto = row.confronto_mese_precedente || {}

        return {
          id_category_motivation: row.id_category_motivation,
          categoria: row.categoria || periodo.categoria || 'Senza categoria',
          totale: periodo.totale ?? 0,
          percentuale_sul_totale: periodo.percentuale_sul_totale ?? 0,
          aperti: periodo.aperti ?? 0,
          percentuale_aperti: periodo.percentuale_aperti ?? 0,
          in_lavorazione: periodo.in_lavorazione ?? 0,
          percentuale_in_lavorazione: periodo.percentuale_in_lavorazione ?? 0,
          chiusi: periodo.chiusi ?? 0,
          percentuale_chiusi: periodo.percentuale_chiusi ?? 0,
          totale_mese_precedente: mesePrecedente.totale ?? 0,
          confronto_totale: confronto.totale || { valore: 0, percentuale: 0 },
        }
      })
    },
    formatPercent(value) {
      const number = Number(value)
      if (Number.isNaN(number)) {
        return '0%'
      }
      return `${number.toFixed(2)}%`
    },
    formatStatCountWithPercent(count, percent) {
      return `${count ?? 0} (${this.formatPercent(percent)})`
    },
    formatConfronto(confronto) {
      if (!confronto) {
        return '0 (0%)'
      }

      const valore = Number(confronto.valore ?? 0)
      const percentuale = Number(confronto.percentuale ?? 0)
      const valoreSign = valore > 0 ? '+' : ''
      const percentualeSign = percentuale > 0 ? '+' : ''
      return `${valoreSign}${valore} (${percentualeSign}${percentuale.toFixed(2)}%)`
    },
    getConfrontoClass(confronto) {
      const valore = Number(confronto?.valore ?? 0)
      if (valore > 0) return 'confronto-positive'
      if (valore < 0) return 'confronto-negative'
      return 'confronto-neutral'
    },
    getConfrontoIcon(confronto) {
      const valore = Number(confronto?.valore ?? 0)
      if (valore > 0) return 'pi pi-arrow-up'
      if (valore < 0) return 'pi pi-arrow-down'
      return 'pi pi-minus'
    },
    getMetricBarStyle(percent) {
      const value = Math.max(0, Math.min(Number(percent) || 0, 100))
      return { width: `${value}%` }
    },
    getCategorieRowClass(data) {
      const classes = []
      if (data?.id_category_motivation === 0) {
        classes.push('stats-categorie-row--uncategorized')
      }
      if (
        this.selectedCategoriaStatsRow
        && this.selectedCategoriaStatsRow.id_category_motivation === data?.id_category_motivation
      ) {
        classes.push('stats-categorie-row--selected')
      }
      return classes.join(' ')
    },
    async loadStatisticheCategorie() {
      this.loadingStatisticheCategorie = true
      this.statisticheCategorieError = null

      try {
        const res = await this.ticketService.getStatisticheCategorie({
          data_da: moment(this.statisticheCategorieDataDa).format('YYYY-MM-DD'),
          data_a: moment(this.statisticheCategorieDataA).format('YYYY-MM-DD'),
        })

        if (res?.success) {
          this.statisticheCategorie = res.data || null
          return
        }

        this.resetStatisticheCategorieResult()
        this.statisticheCategorieError = res?.message || 'Impossibile caricare le statistiche categorie.'
      } catch (err) {
        this.resetStatisticheCategorieResult()
        this.statisticheCategorieError = err?.response?.data?.message || 'Impossibile caricare le statistiche categorie.'
      } finally {
        this.loadingStatisticheCategorie = false
      }
    },
    async loadStatisticheTrasferimenti() {
      this.loadingStatisticheTrasferimenti = true
      this.statisticheTrasferimentiError = null

      try {
        const res = await this.ticketService.getStatisticheTrasferimentiChiamata({
          data_da: moment(this.statisticheTrasferimentiDataDa).format('YYYY-MM-DD'),
          data_a: moment(this.statisticheTrasferimentiDataA).format('YYYY-MM-DD'),
        })

        if (res?.success) {
          this.statisticheTrasferimenti = res.data || null
          return
        }

        this.resetStatisticheTrasferimentiResult()
        this.statisticheTrasferimentiError = res?.message || 'Impossibile caricare le statistiche trasferimenti.'
      } catch (err) {
        this.resetStatisticheTrasferimentiResult()
        this.statisticheTrasferimentiError = err?.response?.data?.message || 'Impossibile caricare le statistiche trasferimenti.'
      } finally {
        this.loadingStatisticheTrasferimenti = false
      }
    },
    async loadStatisticheAgenti() {
      if (!this.selectedAgenteStats?.id) {
        this.statisticheAgentiError = 'Seleziona un agente prima di cercare.'
        return
      }

      this.loadingStatisticheAgenti = true
      this.statisticheAgentiError = null
      this.gestioneTicketCurrentPage = 1

      try {
        const res = await this.ticketService.getStatisticheAgenti({
          id_agente: this.selectedAgenteStats.id,
          data_da: moment(this.statisticheAgentiDataDa).format('YYYY-MM-DD'),
          data_a: moment(this.statisticheAgentiDataA).format('YYYY-MM-DD'),
          page: 1,
          per_page: this.gestioneTicketPerPage,
        })

        if (res?.success) {
          this.applyStatisticheAgentiResponse(res)
          return
        }

        this.resetStatisticheAgentiResult()
        this.statisticheAgentiError = res?.message || 'Impossibile caricare le statistiche agenti.'
      } catch (err) {
        this.resetStatisticheAgentiResult()
        this.statisticheAgentiError = err?.response?.data?.message || 'Impossibile caricare le statistiche agenti.'
      } finally {
        this.loadingStatisticheAgenti = false
      }
    },
  },
}
</script>

<style scoped>
.filter-buttons {
  display: flex;
  gap: 0.75rem;
  margin-top: 1.5rem;
  margin-bottom: 1rem;
  position: sticky;
  top: 0;
  z-index: 10;
  padding: 0.5rem 0;
  background: var(--p-surface-0);
}

.filter-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.6rem 1.25rem;
  font-size: 0.95rem;
  font-weight: 500;
  color: var(--p-text-color-secondary);
  background: var(--p-surface-100);
  border: 1px solid var(--p-surface-300);
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.filter-btn:hover:not(:disabled) {
  background: var(--p-surface-200);
  color: var(--p-text-color);
}

.filter-btn.active {
  background: var(--p-primary-500);
  color: #faad14;
  border-color: var(--p-primary-500);
}

.filter-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.tab-pills-section {
  margin-top: 1rem;
  margin-bottom: 1rem;
}

.tab-pills-section-category {
  margin-top: 1.25rem;
  padding-top: 1rem;
  border-top: 1px solid var(--p-surface-200);
}

.tab-pills-label {
  display: block;
  font-size: 0.9rem;
  font-weight: 600;
  color: var(--p-text-color-secondary);
  margin-bottom: 0.5rem;
}

.filter-empty {
  margin: 0;
  font-size: 0.9rem;
  color: var(--p-text-color-secondary);
}

.tab-pills-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  margin-bottom: 1rem;
}

.tab-pill {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.6rem 1.2rem;
  background: #faad14;
  color: white;
  border: none;
  border-radius: 2rem;
  font-size: 0.95rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
}

.tab-pill:hover {
  background: #faad14;
  transform: translateY(-1px);
}

.tab-pill.active {
  background: #c4880f;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
}

.tab-pill-label {
  white-space: nowrap;
}

.tab-content {
  padding: 1.5rem 0;
  border-top: 1px solid #e0e0e0;
}

.content-loading {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 120px;
  padding: 2rem;
}

.tab-content-placeholder {
  padding: 2rem;
  text-align: center;
}

.andamento-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
}

.andamento-card {
  display: flex;
  flex-direction: column;
  padding: 1rem 1.5rem;
  background: var(--p-surface-100);
  border-radius: 8px;
  min-width: 120px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.andamento-card:hover {
  background: var(--p-surface-200);
}

.andamento-card.active {
  background: var(--p-primary-100);
  outline: 2px solid var(--p-primary-500);
}

.andamento-label {
  font-size: 0.85rem;
  color: var(--p-text-color-secondary);
}

.andamento-value {
  font-size: 1.5rem;
  font-weight: 600;
  margin-top: 0.25rem;
}

.detail-section {
  border-top: 1px solid var(--p-surface-border);
  padding-top: 1.5rem;
}

.detail-title {
  margin: 0 0 1rem 0;
  font-size: 1.1rem;
}

.info-ticket-table {
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
}

.info-ticket-table :deep(.p-datatable-wrapper) {
  border-radius: 12px;
}

.info-ticket-table :deep(.p-datatable-table) {
  border-radius: 12px 12px 0 0;
}

.info-ticket-table :deep(.p-datatable-thead > tr > th) {
  padding: 1rem 1.25rem;
  font-weight: 600;
  font-size: 0.875rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: var(--p-text-color-secondary);
  background: linear-gradient(180deg, var(--p-surface-50) 0%, var(--p-surface-100) 100%);
  border-bottom: 2px solid var(--p-surface-200);
}

.info-ticket-table :deep(.p-datatable-tbody > tr > td) {
  padding: 0.9rem 1.25rem;
  font-size: 0.9rem;
  border-bottom: 1px solid var(--p-surface-100);
  transition: background-color 0.15s ease;
}

.info-ticket-table :deep(.p-datatable-tbody > tr:hover > td) {
  background: var(--p-primary-50) !important;
}

.info-ticket-table :deep(.p-datatable-tbody > tr:last-child > td) {
  border-bottom: none;
}

.info-ticket-table :deep(.p-datatable-tbody > tr > td:first-child) {
  font-weight: 600;
  color: var(--p-primary-600);
}

.info-ticket-table :deep(.p-paginator) {
  padding: 1rem 1.25rem;
  background: var(--p-surface-50);
  border-top: 1px solid var(--p-surface-200);
  border-radius: 0 0 12px 12px;
}

.info-ticket-table :deep(.p-paginator .p-paginator-current) {
  font-weight: 500;
  color: var(--p-text-color-secondary);
}

.status-badge {
  display: inline-block;
  padding: 0.25rem 0.6rem;
  border-radius: 6px;
  font-size: 0.8rem;
  font-weight: 600;
  background: rgba(0, 0, 0, 0.06);
}

.ticket-info-content {
  max-height: 70vh;
  overflow-y: auto;
}

.ticket-info-header {
  margin-bottom: 1.5rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid var(--p-surface-200);
}

.ticket-info-row {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.5rem;
}

.ticket-info-row .label {
  font-weight: 600;
  color: var(--p-primary-600);
}

.ticket-title {
  margin: 0.5rem 0;
  font-size: 1.1rem;
}

.ticket-meta {
  font-size: 0.85rem;
  color: var(--p-text-color-secondary);
}

.ticket-meta span + span {
  margin-left: 0.5rem;
}

.ticket-contestazione {
  margin-top: 0.75rem;
  padding: 0.75rem;
  background: var(--p-surface-100);
  border-radius: 8px;
  font-size: 0.9rem;
}

.ticket-message {
  padding: 0.75rem 0;
  border-bottom: 1px solid var(--p-surface-100);
}

.ticket-message:last-child {
  border-bottom: none;
}

.message-meta {
  display: flex;
  justify-content: space-between;
  margin-bottom: 0.35rem;
  font-size: 0.85rem;
  color: var(--p-text-color-secondary);
}

.message-content {
  white-space: pre-wrap;
  font-size: 0.9rem;
}

.ticket-section {
  margin-top: 1.5rem;
}

.ticket-section h5 {
  margin: 0 0 0.75rem 0;
  font-size: 0.95rem;
}
.card-stat-utente{
  width: 250px;
}

.statistiche-agenti-section {
  border-top: 1px solid #e0e0e0;
}

.statistiche-agenti-title {
  margin: 0 0 1rem 0;
  font-size: 1.1rem;
}

.statistiche-agenti-filters {
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  align-items: flex-end;
}

.statistiche-agenti-filters .field-group {
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
  min-width: 180px;
  flex: 1;
}

.statistiche-agenti-summary {
  display: flex;
  flex-wrap: wrap;
  gap: 1.5rem;
  margin-top: 1rem;
  padding: 1rem;
  background: var(--p-surface-100);
  border-radius: 8px;
}

.summary-item {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.summary-label {
  font-size: 0.85rem;
  color: var(--p-text-color-secondary);
}

.summary-value {
  font-weight: 600;
}

.response-box {
  border: 1px solid transparent;
  border-radius: 8px;
  padding: 0.85rem 1rem;
}

.response-box--error {
  background: #fef2f2;
  border-color: #fecaca;
}

.feedback-message {
  margin: 0;
}

.statistiche-agenti-table {
  border-radius: 8px;
  overflow: hidden;
}

.confronto-positive {
  color: #15803d;
  font-weight: 600;
}

.confronto-negative {
  color: #b91c1c;
  font-weight: 600;
}

.confronto-neutral {
  color: var(--p-text-color-secondary);
}

.stats-categorie-period-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 1rem;
  margin-top: 1rem;
}

.stats-period-card {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem 1.15rem;
  border-radius: 14px;
  border: 1px solid rgba(13, 60, 97, 0.1);
  background: linear-gradient(135deg, #f8fbff 0%, #ffffff 100%);
  box-shadow: 0 10px 24px rgba(13, 60, 97, 0.06);
}

.stats-period-card--muted {
  background: linear-gradient(135deg, #f7f8fa 0%, #ffffff 100%);
}

.stats-period-card-icon {
  width: 42px;
  height: 42px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(13, 60, 97, 0.08);
  color: #0d3c61;
  flex-shrink: 0;
}

.stats-period-card-content {
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
  min-width: 0;
}

.stats-period-card-label {
  font-size: 0.78rem;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #64748b;
}

.stats-period-card-value {
  font-size: 0.95rem;
  font-weight: 600;
  color: #0f172a;
}

.stats-categorie-kpi-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 1rem;
  margin-top: 1.25rem;
}

.stats-kpi-card {
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  padding: 1.1rem 1.15rem;
  border-radius: 16px;
  border: 1px solid rgba(13, 60, 97, 0.08);
  background: #ffffff;
  box-shadow: 0 12px 28px rgba(15, 23, 42, 0.05);
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.stats-kpi-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 16px 32px rgba(15, 23, 42, 0.08);
}

.stats-kpi-card--total {
  border-color: rgba(13, 60, 97, 0.18);
  background: linear-gradient(180deg, #f4f8fc 0%, #ffffff 100%);
}

.stats-kpi-card--opened {
  border-left: 4px solid #d97706;
}

.stats-kpi-card--progress {
  border-left: 4px solid #2563eb;
}

.stats-kpi-card--closed {
  border-left: 4px solid #15803d;
}

.stats-kpi-label {
  font-size: 0.78rem;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #64748b;
}

.stats-kpi-value {
  font-size: 1.75rem;
  line-height: 1;
  font-weight: 700;
  color: #0f172a;
  font-variant-numeric: tabular-nums;
}

.stats-kpi-sub {
  font-size: 0.82rem;
  color: #64748b;
}

.stats-categorie-table-panel {
  margin-top: 1.5rem;
  border-radius: 18px;
  border: 1px solid rgba(13, 60, 97, 0.1);
  background: #ffffff;
  box-shadow: 0 16px 36px rgba(13, 60, 97, 0.07);
  overflow: hidden;
}

.stats-categorie-table-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 1.15rem 1.35rem;
  border-bottom: 1px solid rgba(13, 60, 97, 0.08);
  background: linear-gradient(180deg, #f8fbff 0%, #ffffff 100%);
}

.stats-categorie-table-title {
  margin: 0;
  font-size: 1rem;
  font-weight: 700;
  color: #0d3c61;
}

.stats-categorie-table-subtitle {
  margin: 0.25rem 0 0;
  font-size: 0.85rem;
  color: #64748b;
}

.stats-categorie-table {
  border-radius: 0;
}

.stats-categorie-table :deep(.p-datatable-wrapper) {
  border-radius: 0;
}

.stats-categorie-table :deep(.p-datatable-thead > tr > th) {
  padding: 0.95rem 1.2rem;
  font-size: 0.74rem;
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: #475569;
  background: #f8fafc;
  border-bottom: 1px solid #e2e8f0;
}

.stats-categorie-table :deep(.p-datatable-tbody > tr > td) {
  padding: 1rem 1.2rem;
  border-bottom: 1px solid #f1f5f9;
  vertical-align: middle;
  transition: background-color 0.18s ease;
}

.stats-categorie-table :deep(.p-datatable-tbody > tr:hover > td) {
  background: #f8fbff;
}

.stats-categorie-table :deep(.stats-categorie-row--uncategorized > td) {
  background: #fafafa;
}

.stats-categorie-table-clickable :deep(.p-datatable-tbody > tr) {
  cursor: pointer;
}

.stats-categorie-table-clickable :deep(.p-datatable-tbody > tr:hover > td) {
  background: #f0f7ff !important;
}

.stats-categorie-table :deep(.stats-categorie-row--selected > td) {
  background: #e8f2fb !important;
}

.categorie-tickets-dialog :deep(.p-dialog) {
  width: min(96vw, 1420px) !important;
  max-width: 96vw;
}

.categorie-tickets-dialog :deep(.p-dialog-content) {
  padding-top: 0.75rem;
  overflow-x: hidden;
}

.categorie-tickets-dialog-content {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.categorie-tickets-summary {
  display: flex;
  flex-wrap: wrap;
  gap: 1rem 1.5rem;
  padding: 0.85rem 1rem;
  border-radius: 10px;
  background: #f8fafc;
  color: #475569;
  font-size: 0.9rem;
}

.categorie-tickets-filters {
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
  max-width: 260px;
}

.categorie-tickets-filters label {
  font-size: 0.82rem;
  font-weight: 600;
  color: #64748b;
}

.categorie-tickets-status-filter {
  width: 100%;
}

.categorie-tickets-table {
  margin-top: 0.25rem;
  width: 100%;
}

.categorie-tickets-table :deep(.p-datatable-table) {
  width: 100%;
  table-layout: fixed;
}

.categorie-tickets-table :deep(.p-datatable-tbody > tr > td),
.categorie-tickets-table :deep(.p-datatable-thead > tr > th) {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.categorie-tickets-table :deep(td:nth-child(3)),
.categorie-tickets-table :deep(th:nth-child(3)) {
  white-space: normal;
  word-break: break-word;
}

.stats-category-cell {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
}

.stats-category-dot {
  width: 10px;
  height: 10px;
  border-radius: 999px;
  margin-top: 0.35rem;
  flex-shrink: 0;
}

.stats-category-dot--accent {
  background: #0d3c61;
  box-shadow: 0 0 0 4px rgba(13, 60, 97, 0.12);
}

.stats-category-dot--muted {
  background: #94a3b8;
}

.stats-category-text {
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
  min-width: 0;
}

.stats-category-name {
  font-weight: 600;
  color: #0f172a;
  line-height: 1.35;
}

.stats-category-share {
  font-size: 0.78rem;
  color: #64748b;
}

.stats-metric-cell {
  display: flex;
  flex-direction: column;
  gap: 0.45rem;
  min-width: 90px;
}

.stats-metric-value {
  font-size: 1.05rem;
  font-weight: 700;
  color: #0f172a;
  font-variant-numeric: tabular-nums;
}

.stats-metric-bar {
  height: 6px;
  border-radius: 999px;
  background: #e2e8f0;
  overflow: hidden;
}

.stats-metric-bar-fill {
  height: 100%;
  border-radius: 999px;
  transition: width 0.35s ease;
}

.stats-metric-bar-fill--total {
  background: linear-gradient(90deg, #0d3c61 0%, #1a5490 100%);
}

.stats-compare-badge {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.35rem 0.65rem;
  border-radius: 999px;
  font-size: 0.8rem;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}

.stats-compare-badge.confronto-positive {
  color: #166534;
  background: #ecfdf3;
}

.stats-compare-badge.confronto-negative {
  color: #b91c1c;
  background: #fef2f2;
}

.stats-compare-badge.confronto-neutral {
  color: #475569;
  background: #f1f5f9;
}

.stats-status-pill {
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  padding: 0.4rem 0.7rem;
  border-radius: 10px;
  font-size: 0.9rem;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
}

.stats-status-pill small {
  font-size: 0.74rem;
  font-weight: 600;
  opacity: 0.85;
}

.stats-status-pill--opened {
  color: #9a3412;
  background: #fff7ed;
}

.stats-status-pill--progress {
  color: #1d4ed8;
  background: #eff6ff;
}

.stats-status-pill--closed {
  color: #166534;
  background: #ecfdf3;
}

.stats-prev-month {
  display: inline-flex;
  min-width: 2.5rem;
  justify-content: center;
  padding: 0.35rem 0.55rem;
  border-radius: 8px;
  background: #f8fafc;
  color: #334155;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
}

.stats-categorie-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  padding: 2.5rem 1rem;
  color: #64748b;
}

.stats-categorie-empty .pi {
  font-size: 1.5rem;
  color: #94a3b8;
}

@media (max-width: 1100px) {
  .stats-categorie-kpi-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 640px) {
  .stats-categorie-kpi-grid {
    grid-template-columns: 1fr;
  }

  .stats-categorie-table-header {
    padding: 1rem;
  }
}

.statistiche-agenti-categorie-header {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  width: 100%;
}

.statistiche-agenti-categorie-filters {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  align-items: center;
}

.categoria-stats-filter {
  min-width: 240px;
}

.categoria-stats-search {
  min-width: 200px;
}

.gestione-ticket-dialog :deep(.p-dialog-content) {
  padding-top: 0.5rem;
}

.gestione-dialog {
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
}

.gestione-dialog-hero {
  padding: 1rem 1.15rem;
  border-radius: 10px;
  background: var(--p-surface-50);
  border: 1px solid var(--p-surface-200);
}

.gestione-dialog-hero-top {
  display: flex;
  align-items: center;
  gap: 0.65rem;
  margin-bottom: 0.5rem;
}

.gestione-dialog-id {
  font-size: 0.8rem;
  font-weight: 700;
  letter-spacing: 0.02em;
  color: var(--p-primary-600);
}

.gestione-dialog-titolo {
  margin: 0;
  font-size: 1.05rem;
  font-weight: 600;
  line-height: 1.4;
  color: var(--p-text-color);
}

.gestione-dialog-section {
  padding: 0 0.15rem;
}

.gestione-dialog-section--last {
  margin-bottom: 0.25rem;
}

.gestione-section-title {
  display: flex;
  align-items: center;
  gap: 0.45rem;
  margin: 0 0 0.75rem 0;
  font-size: 0.78rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: var(--p-text-color-secondary);
}

.gestione-section-title .pi {
  font-size: 0.85rem;
}

.gestione-info-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.75rem;
}

.gestione-info-cell {
  padding: 0.75rem 0.9rem;
  border-radius: 8px;
  background: var(--p-surface-0);
  border: 1px solid var(--p-surface-200);
}

.gestione-info-cell--highlight {
  grid-column: 1 / -1;
  background: var(--p-primary-50);
  border-color: var(--p-primary-100);
}

.gestione-info-label {
  display: block;
  margin-bottom: 0.3rem;
  font-size: 0.75rem;
  font-weight: 500;
  color: var(--p-text-color-secondary);
}

.gestione-info-value {
  display: block;
  font-size: 0.92rem;
  font-weight: 600;
  line-height: 1.35;
  color: var(--p-text-color);
}

.gestione-info-value--large {
  font-size: 1.1rem;
  color: var(--p-primary-700);
}

.gestione-stat-cards {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.75rem;
}

.gestione-stat-card {
  display: flex;
  align-items: flex-start;
  gap: 0.7rem;
  padding: 0.9rem 1rem;
  border-radius: 8px;
  border: 1px solid var(--p-surface-200);
  background: var(--p-surface-0);
}

.gestione-stat-dot {
  flex-shrink: 0;
  width: 10px;
  height: 10px;
  margin-top: 0.35rem;
  border-radius: 50%;
}

.gestione-stat-card--opened .gestione-stat-dot {
  background: #2563eb;
}

.gestione-stat-card--in-progress .gestione-stat-dot {
  background: #d97706;
}

.gestione-stat-content {
  min-width: 0;
}

.gestione-stat-label {
  display: block;
  margin-bottom: 0.25rem;
  font-size: 0.75rem;
  color: var(--p-text-color-secondary);
}

.gestione-stat-value {
  display: block;
  font-size: 1.05rem;
  font-weight: 700;
  line-height: 1.3;
  color: var(--p-text-color);
}

.gestione-transizioni-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
  gap: 0.65rem;
}

.gestione-transizione-card {
  padding: 0.8rem 0.9rem;
  border-radius: 8px;
  border: 1px solid var(--p-surface-200);
  background: var(--p-surface-50);
}

.gestione-transizione-route {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.35rem;
  margin-bottom: 0.45rem;
  font-size: 0.78rem;
  font-weight: 600;
  color: var(--p-text-color-secondary);
}

.gestione-transizione-route .pi {
  font-size: 0.65rem;
  opacity: 0.7;
}

.gestione-transizione-durata {
  font-size: 1rem;
  font-weight: 700;
  color: var(--p-text-color);
}

.gestione-transizione-gestore {
  display: flex;
  align-items: center;
  gap: 0.35rem;
  margin-top: 0.35rem;
  font-size: 0.8rem;
  color: var(--p-text-color-secondary);
}

.gestione-transizione-gestore .pi {
  font-size: 0.75rem;
}

.gestione-dialog-footer {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 0.5rem;
  width: 100%;
}

.gestione-ticket-table-clickable :deep(.p-datatable-tbody > tr) {
  cursor: pointer;
}

.gestione-ticket-table-clickable :deep(.p-datatable-tbody > tr:hover > td) {
  background: var(--p-primary-50) !important;
}

.gestione-ticket-id {
  font-weight: 600;
  color: var(--p-primary-600);
}

.gestione-ticket-filter-hint {
  font-size: 0.85rem;
  font-weight: 500;
  color: var(--p-text-color-secondary);
}

.gestione-passaggi-table {
  margin-top: 0;
}

@media (max-width: 640px) {
  .gestione-info-grid,
  .gestione-stat-cards {
    grid-template-columns: 1fr;
  }

  .gestione-info-cell--highlight {
    grid-column: auto;
  }
}

.status-badge--opened {
  color: #2563eb;
  background-color: rgba(37, 99, 235, 0.12);
}

.status-badge--in-progress {
  color: #d97706;
  background-color: rgba(217, 119, 6, 0.12);
}

.status-badge--closed {
  color: #16a34a;
  background-color: rgba(22, 163, 74, 0.12);
}

.stats-trasferimenti-kpi-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 1rem;
  margin-top: 1rem;
}

.stats-trasferimenti-kpi-card {
  display: flex;
  flex-direction: column;
  gap: 0.65rem;
  padding: 1.1rem 1.2rem;
  border-radius: 14px;
  border: 1px solid rgba(13, 60, 97, 0.1);
  background: #fff;
  box-shadow: 0 10px 24px rgba(13, 60, 97, 0.06);
}

.stats-trasferimenti-kpi-card--received {
  border-top: 3px solid #2563eb;
}

.stats-trasferimenti-kpi-card--made {
  border-top: 3px solid #7c3aed;
}

.stats-trasferimenti-kpi-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.75rem;
}

.stats-trasferimenti-kpi-breakdown {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.stats-transfer-pill {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.28rem 0.6rem;
  border-radius: 999px;
  font-size: 0.82rem;
  font-weight: 600;
}

.stats-transfer-pill small {
  font-weight: 500;
  opacity: 0.85;
}

.stats-transfer-pill--success {
  color: #166534;
  background: #ecfdf3;
}

.stats-transfer-pill--failed {
  color: #b91c1c;
  background: #fef2f2;
}

.stats-metric-bar-fill--received {
  background: linear-gradient(90deg, #3b82f6, #60a5fa);
}

.stats-metric-bar-fill--made {
  background: linear-gradient(90deg, #7c3aed, #a78bfa);
}

.stats-prev-month-block {
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
  font-size: 0.82rem;
  color: var(--p-text-color-secondary);
  font-weight: 600;
}

.stats-trasferimenti-table :deep(.p-datatable-thead > tr > th) {
  white-space: nowrap;
}
</style>
